import Phaser from 'phaser';
import PageSceneBase from './PageSceneBase';
import VirtualKeyboardScene from './VirtualKeyboardScene';
import { v4 as uuidv4 } from 'uuid';

import '../assets/fonts/wingdings2.ttf';
import { ColorsDM, iteamColorModelInstance, ITeamModes, iteamWorkingModeInstance, normalizeUserInput } from '../datamodel/datamodel';


var isFontWingDings2Loaded = false;

function loadFont(name, url) {
    var newFont = new FontFace(name, `url(${url})`);
    newFont.load().then(function (loaded) {
        document.fonts.add(loaded);
    }).catch(function (error) {
        return error;
    });
}


function toHexadecimalStringColorCode(number) {
    return '#' + number.toString(16).padStart(6, '0').toUpperCase();
}

class PageExerciseScene extends PageSceneBase {

    constructor(param) {
        super({key: 'PageExerciseScene', config: param.config});
        super.closeDrawingThicknessMenuCallback = param.setCloseDrawingThicknessMenuCallback;

        this.exerciseDM = param.exerciseDM;
        this.setResizeEnabled = param.setResizeEnabled;
        super.resizeHandler = param.resizeHandler;
        this.pressedSourceButtonDM = null;
        this.selectionBox = null;


        this.submitTextBoxValue = this.submitTextBoxValue.bind(this);
    }

    preload() {
        super.preload();

        this.load.image('blurifyPatternImage', './iteam/blurify/blurify.png');
        this.load.image('correctIndicatorImage', './iteam/result_indicators/res_indic_correct.png');
        this.load.image('incorrectIndicatorImage', './iteam/result_indicators/res_indic_incorrect.png');
        
        if (!isFontWingDings2Loaded) {
            loadFont('wingdings2', './iteam/fonts/wingdings2.ttf');
            isFontWingDings2Loaded = true;
        }
    }

    create() {
        super.create();
        this.addPage();
        this.events.once('destroy', this.destroyScene, this);
    }


    update() {
        super.update();
    }

    removePage() {
        if (this.pageImage !== null) {  // then remove page itself
            this.pageImage.destroy();
            this.pageImage = null;
        }
        this.textures.remove('exerciseCanvas');
    }

    addPage() {
        if (!this.textures.exists('exerciseCanvas')) {
            return;
        }

        // first find existing VirtualKeyboardScene instance and remove it from scene manager
        this.game.scene.scenes.forEach(sceneIterator => {
            if (sceneIterator instanceof VirtualKeyboardScene) {
                this.game.scene.remove('VirtualKeyboardScene');
                return;
            }
        });

        this.pageImage = this.add.sprite(0, 0, 'exerciseCanvas').setOrigin(0);
        this.contentContainer.add(this.pageImage);

        this.restartExerciseHandler();
        
        this.prepareDrawingBrush();
        this.addDrawingCanvases();

        this.setResizeEnabled(true);
        let displayWidthHeightRatio = this.config.width / this.config.height;
        let pageWidthHeightRatio = this.pageImage.width / this.pageImage.height;

        if (displayWidthHeightRatio > pageWidthHeightRatio) {
            this.zoomFitHeight();
        } else {
            this.zoomFitWidth();
        }
    }
    
    returnToPageButtonClickHandler() {
        
        let pageScene = this.scene.get('PageScene');
        pageScene.currentShowingExerciseSequence = -1;
        this.scene.setVisible(true, 'PageScene');
        this.scene.resume('PageScene');
        
        this.removePage();
        this.scene.stop();
        this.game.scene.remove('PageExerciseScene');
        pageScene.pageExerciseScene = null;
        pageScene.setExerciseSceneHandler(null);
        pageScene.setExerciseCloseCallback(null);
        iteamColorModelInstance.setChangeColorCallBack(pageScene.prepareDrawingBrush);
        iteamWorkingModeInstance.setChangeModeOrSizeCallBack(pageScene.prepareDrawingBrush);
    }

    restartExerciseHandler() {
        this.createBlurifiedRectangles();
        this.createPreloadedLabels();
        this.removeInteractionButtons();
        this.createInteractionButtons();
    }

    createBlurifiedRectangles() {
        if (this.exerciseDM === null) {
            return;
        }
        this.exerciseDM.zoomInits.forEach(zoomInitDM => {
            zoomInitDM.blurifies.map(blurifyDM => {
                if (blurifyDM.visualSprite !== null) {
                    blurifyDM.visualSprite.destroy();
                    blurifyDM.visualSprite = null;
                }
    
                let lpBlurifyDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, blurifyDM.x, blurifyDM.y);

                let imageBlurify = this.add.image(lpBlurifyDM.x, lpBlurifyDM.y, 'blurifyPatternImage').setOrigin(0);
                
                imageBlurify.setCrop(0, 0, blurifyDM.w, blurifyDM.h);
                this.contentContainer.add(imageBlurify);
                blurifyDM.visualSprite = imageBlurify;
                return null;
            });
        });
    }


    createPreloadedLabels() {

        if (this.exerciseDM === null) {
            return;
        }

        if (this.exerciseDM.preloadedLabels === null) {
            return;
        }

        this.exerciseDM.preloadedLabels.forEach(labelDM => {

            if (labelDM.textObject !== null) {
                labelDM.textObject.destroy();
                labelDM.textObject = null;
            }

            if (labelDM.skip) {
                return;
            }

            let lpLabelDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, labelDM.x, labelDM.y);


            let fontSize = this.exerciseDM.config.fontSize;
            let colorcode = iteamColorModelInstance.getColor().colorcode;
            let text = this.createTextObject(lpLabelDM.x, lpLabelDM.y,
                                    labelDM.w, labelDM.h, labelDM.value, labelDM.align, fontSize, colorcode, 'Arial', labelDM.rotation);
            this.contentContainer.add(text);
            labelDM.textObject = text;
        });
    }


    createInteractionButtons() {
        if (this.exerciseDM === null) {
            return;
        }

        if (this.exerciseDM.questions === null) {
            return;
        }

        this.exerciseDM.questions.forEach(questionDM => {
            if (questionDM.buttons === null) {
                return;
            }
            questionDM.buttons.forEach((buttonDM, index) => {
                let lp = this.calculateLocalXYFromGlobalXY(this.exerciseDM, buttonDM.x, buttonDM.y);

                let rectangle = this.add.rectangle(lp.x, lp.y, 
                                                buttonDM.w, buttonDM.h, 0xff0000, 0.25).setOrigin(0);
                rectangle.setDataEnabled(true);
                rectangle.setStrokeStyle(2, 0x8855ff);
                rectangle.setInteractive();
                rectangle.on('pointerdown', () => { this.sourceButtonClickListenerFunction(this.exerciseDM, 
                                                                                            questionDM, buttonDM) }, this);
                rectangle.angle = buttonDM.rotation;

                this.contentContainer.add(rectangle);
                if (questionDM.type !== 'coloring') {
                    let tween = this.tweens.add({
                        targets: rectangle,
                        alpha: {getStart: () => {return 0.0001}, getEnd: () => {return 0.6}},
                        yoyo: true,
                        repeat: -1,
                        ease: 'easeInQuint'
                    });
                    rectangle.data.set('tween', tween);    
                } else {
                    rectangle.alpha = 0.001;
                }

                buttonDM.visualButton = rectangle;
            });
        });

        this.exerciseDM.destinationButtons.map(buttonDM => {

            let lp = this.calculateLocalXYFromGlobalXY(this.exerciseDM, buttonDM.x, buttonDM.y);

            let rectangle = this.add.rectangle(lp.x + buttonDM.w / 2, lp.y + buttonDM.h / 2, 
                                            buttonDM.w, buttonDM.h, 0xff0000, 0.25).setOrigin(0.5);
            rectangle.setDataEnabled(true);
            rectangle.setStrokeStyle(2, 0x8855ff);
            rectangle.setInteractive();
            rectangle.on('pointerdown', () => { this.destinationButtonClickListenerFunction(this.exerciseDM, buttonDM) }, this);
            rectangle.angle = buttonDM.rotation;
            rectangle.setVisible(false);

            this.contentContainer.add(rectangle);
            let tween = this.tweens.add({
                targets: rectangle,
                angle: {getStart: () => {return -4}, getEnd: () => {return 4}},
                yoyo: true,
                repeat: -1,
                duration: 300,
                ease: 'easeInQuint'
            });
            tween.pause(); // destination buttons are not paused, they are stopped
            rectangle.data.set('tween', tween);
            buttonDM.visualButton = rectangle;

            return null;
        });

        if (this.exerciseDM.puzzleFindWordsDM !== null) {
            let pfwDM = this.exerciseDM.puzzleFindWordsDM;

            if (pfwDM.visualContainer !== null) {
                // already defined visual container must be removed first, then create new one
                // TODO: resetting the model to default values must be handled here.
                pfwDM.buttons.forEach(rowArr => {
                    rowArr.forEach(buttonDMTmp => {
                        if (buttonDMTmp.visualButton === null) {
                            return;
                        }
                        buttonDMTmp.visualButton.destroy();
                        buttonDMTmp.visualButton = null;
                    });
                });

                pfwDM.visualContainer.destroy();
                pfwDM.visualContainer = null;
            }

            let lpPfwDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, pfwDM.x, pfwDM.y);

            let visualContainer = this.add.container(lpPfwDM.x, lpPfwDM.y);

            pfwDM.buttons.forEach(rowArr => {
                rowArr.forEach(buttonDMTmp => {
                    let rectangle = this.add.rectangle(buttonDMTmp.x + buttonDMTmp.w / 2,
                                                        buttonDMTmp.y + buttonDMTmp.h / 2, 
                                                        buttonDMTmp.w, buttonDMTmp.h, 0xff0000, 0.25).setOrigin(0.5);
                    rectangle.setDataEnabled(true);
                    rectangle.setStrokeStyle(2, 0x8855ff);
                    rectangle.setInteractive();
                    rectangle.on('pointerdown', () => { 
                            this.sourceButtonFindTheWordsClickListenerFunction(pfwDM, buttonDMTmp)
                        }, this);
                    rectangle.setVisible(true);
                    let tween = this.tweens.add({
                        targets: rectangle,
                        alpha: {getStart: () => {return 0.00001}, getEnd: () => {return 0.6}},
                        yoyo: true,
                        repeat: -1,
                        ease: 'easeInQuint'
                    });
                    rectangle.data.set('tweenSrc', tween);


                    tween = this.tweens.add({
                        targets: rectangle,
                        angle: {getStart: () => {return -4}, getEnd: () => {return 4}},
                        yoyo: true,
                        repeat: -1,
                        duration: 300,
                        ease: 'easeInQuint'
                    });
                    rectangle.data.set('tweenDst', tween);
                    tween.pause();
                    
                    buttonDMTmp.visualButton = rectangle;
                    visualContainer.add(rectangle);
                });
            });
            visualContainer.angle = pfwDM.rotation;
            this.contentContainer.add(visualContainer);
            pfwDM.visualContainer = visualContainer;
        }

    }

    removeInteractionButtons() {
        if (this.exerciseDM === null) {
            return;
        }

        if (this.exerciseDM.questions === null) {
            return;
        }

        this.exerciseDM.questions.forEach(questionDM => {
            questionDM.isAnswerShown = false;
            questionDM.isChecked = false;

            if (questionDM.buttons === null) {
                return;
            }

            questionDM.buttons.forEach((buttonDM, index) => {
                if (buttonDM.visualButton === undefined || buttonDM.visualButton === null) {
                    return;
                }

                let rectangle = buttonDM.visualButton;
                let tween = rectangle.data.get('tween');
                if (tween !== undefined && tween !== null) {
                    this.tweens.remove(tween);
                    rectangle.data.remove('tween');
                }
                let visualSprite = rectangle.data.get('visualSprite');
                if (visualSprite !== undefined && visualSprite !== null) {
                    visualSprite.destroy();
                    rectangle.data.remove('visualSprite')
                }
                let textObject = rectangle.data.get('textObject');
                if (textObject !== undefined && textObject !== null) {
                    textObject.destroy();
                    rectangle.data.remove('textObject')
                }

                buttonDM.labelModels.forEach(labelDM => {
                    if (labelDM.textObject === undefined || labelDM.textObject === null) {
                        return;
                    }
                    labelDM.textObject.destroy();
                    labelDM.textObject = null;
                });

                this.contentContainer.remove(rectangle);
                rectangle.destroy();
                buttonDM.visualButton = null;
                buttonDM.currentAnswers = [];

                if (buttonDM.visualResultIndicator !== null) {
                    let indicator = buttonDM.visualResultIndicator;
                    this.contentContainer.remove(indicator);
                    indicator.destroy();
                    buttonDM.visualResultIndicator = null;
                }
            });
        });

        this.exerciseDM.destinationButtons.forEach(buttonDM => {
            if (buttonDM.visualButton === undefined || buttonDM.visualButton === null) {
                return;
            }

            let rectangle = buttonDM.visualButton;
            let tween = rectangle.data.get('tween');
            if (tween !== undefined && tween !== null) {
                this.tweens.remove(tween);
                rectangle.data.remove('tween');
            }
            let visualSprite = rectangle.data.get('visualSprite');
            if (visualSprite !== undefined && visualSprite !== null) {
                visualSprite.destroy();
                rectangle.data.remove('visualSprite')
            }

            this.contentContainer.remove(rectangle);
            rectangle.destroy();
            buttonDM.visualButton = null;
            buttonDM.currentAnswers = [];
        });

        if (this.exerciseDM.puzzleFindWordsDM !== null) {
            let pfwDM = this.exerciseDM.puzzleFindWordsDM;
            if (pfwDM.visualContainer !== null) {
                pfwDM.buttons.forEach(rowArr => {
                    rowArr.forEach(buttonDMTmp => {
                        if (buttonDMTmp.visualButton !== null) {
                            let tween = buttonDMTmp.visualButton.data.get('tweenSrc');
                            if (tween !== undefined && tween !== null) {
                                buttonDMTmp.visualButton.data.remove('tweenSrc');
                                this.tweens.remove(tween);
                            }
                            tween = buttonDMTmp.visualButton.data.get('tweenDst');
                            if (tween !== undefined && tween !== null) {
                                buttonDMTmp.visualButton.data.remove('tweenDst');
                                this.tweens.remove(tween);
                            }
                            buttonDMTmp.visualButton.destroy();
                            buttonDMTmp.visualButton = null;
                        }

                        buttonDMTmp.currentAnswers.forEach(answerDM => {
                            if (answerDM.visualSprite !== undefined && answerDM.visualSprite !== null) {
                                answerDM.visualSprite.destroy();
                                answerDM.visualSprite = null;
                            }
                            if (answerDM.visualResultIndicator !== undefined && answerDM.visualResultIndicator !== null) {
                                answerDM.visualResultIndicator.destroy();
                                answerDM.visualResultIndicator = null;
                            }
                        });
                        buttonDMTmp.currentAnswers = [];
    
                    });
                });

                pfwDM.visualContainer.destroy();
                pfwDM.visualContainer = null;
            }
        }
        
    }

    sourceButtonClickListenerFunction(exerciseDM, questionDM, buttonDM) {

        if (iteamWorkingModeInstance.getMode() !== ITeamModes.interaction) {
            return;
        }

        if (questionDM.isAnswerShown === true) {
            return;
        }

        if (questionDM.isChecked === true) {
            return;
        }

        if (questionDM.type === 'circle_the_correct_one') {
            this.circleTheCorrectOneButtonClickHelper(exerciseDM, questionDM, buttonDM);
        } else if (questionDM.type === 'put_a_tick_into_the_correct_one') {
            this.putATickIntoTheCorrectOneButtonClickHelper(exerciseDM, questionDM, buttonDM);
        } else if (questionDM.type === 'match_and_write_to_a_box') {
            this.matchSrcButtonClickHelper(exerciseDM, buttonDM);
        } else if (questionDM.type === 'match_and_write_to_a_group_box') {
            this.matchSrcButtonClickHelper(exerciseDM, buttonDM);
        } else if (questionDM.type === 'match_and_draw_line_horizontal') {
            this.matchSrcButtonClickHelper(exerciseDM, buttonDM);
        } else if (questionDM.type === 'match_and_draw_line_vertical') {
            this.matchSrcButtonClickHelper(exerciseDM, buttonDM);
        } else if (questionDM.type === 'match_words_to_make_sentence') {
            this.matchSrcButtonClickHelper(exerciseDM, buttonDM);
        } else if (questionDM.type === 'fill_in_the_blanks' || questionDM.type === 'fill_in_the_blanks_grouping' ) {
            this.requestTextBox(exerciseDM, buttonDM);
        } else if (questionDM.type === 'crossword_puzzle') {
            this.requestTextBox(exerciseDM, buttonDM);
        } else if (questionDM.type === 'write_t_or_f'
                    || questionDM.type === 'write_y_or_n'
                    || questionDM.type === 'write_true_or_false'
                    || questionDM.type === 'write_tick_or_cross'
                    || questionDM.type === 'write_from_selection_box'
                    || questionDM.type === 'write_yes_or_no') {
            
            this.selectionBoxGenerateHelper(exerciseDM, questionDM, buttonDM)
            
        } else if (questionDM.type === 'coloring') {
            this.coloringButtonClickHelper(exerciseDM);
        }
        
    }

    coloringButtonClickHelper(exerciseDM) {
        let zoomInitX = this.exerciseDM.zoomInits[0].x;
        let zoomInitY = this.exerciseDM.zoomInits[0].y;

        let activePointer = this.game.input.activePointer;

        let clickXonExercise = (activePointer.x - this.contentContainer.x) / this.contentContainer.scale + zoomInitX;
        let clickYonExercise = (activePointer.y - this.contentContainer.y) / this.contentContainer.scale + zoomInitY;

        exerciseDM.questions.map(questionDMTmp => {
            if (questionDMTmp.type !== 'coloring') {
                return null;
            }
            questionDMTmp.buttons.map(buttonDMTmp => {
                let isPointInPolygon = this.isPointInPolygon(clickXonExercise, clickYonExercise, buttonDMTmp.pathModels);
                if (!isPointInPolygon) {
                    return null;
                }
                let polygon = buttonDMTmp.visualButton.data.get('visualSprite');
                if (polygon !== undefined) {
                    polygon.destroy();
                    buttonDMTmp.currentAnswers = [];
                }
                let colorcode = iteamColorModelInstance.getColor().colorcode;
                let colorname = iteamColorModelInstance.getColor().colorname;
                polygon = this.createColoringPolygon(buttonDMTmp.x - zoomInitX, buttonDMTmp.y - zoomInitY, 
                    zoomInitX, zoomInitY, buttonDMTmp.pathModels, colorcode);
                this.contentContainer.add(polygon);
                buttonDMTmp.currentAnswers.push(colorname);
                buttonDMTmp.visualButton.data.set('visualSprite', polygon);
                return null;
            });
            return null;
        });

    }


    selectionBoxGenerateHelper(exerciseDM, questionDM, buttonDM) {

        if (this.selectionBox !== null) {
            let sbButtonDM = this.selectionBox.config.buttonDM;
            this.selectionBox.destroy();
            this.selectionBox = null;
            if (sbButtonDM === buttonDM) { // the same is selected
                this.pressedSourceButtonDM = null;
                return;
            }
        }

        this.matchSrcButtonClickHelper(exerciseDM, buttonDM);


        let maxCharCountPerItem = 8;
        questionDM.alternateLabels.forEach(item => {
            if (item.length > maxCharCountPerItem) {
                maxCharCountPerItem = item.length;
            }
        });

        let calculatedWidth = 180;
        if (maxCharCountPerItem > 8) {
            if (maxCharCountPerItem * 17 > calculatedWidth) {
                calculatedWidth = maxCharCountPerItem * 17;
            }
        }

        let itemHeight = (40 * (questionDM.selectionBoxFontSize / 32));
        let itemWidth = (calculatedWidth * (questionDM.selectionBoxFontSize / 32));

        let config = {
            exerciseDM: exerciseDM,
            questionDM: questionDM,
            buttonDM: buttonDM,
            itemWidth: itemWidth,
            itemHeight: itemHeight,
            fontSize: questionDM.selectionBoxFontSize,
            items: this.generateSelectionBoxItemsConfig(questionDM)
        };
        
        this.pressedSourceButtonDM = buttonDM;

        let menuHeight = config.items.length * config.itemHeight;

        let lpButtonDM = this.calculateLocalXYFromGlobalXY(exerciseDM, buttonDM.x, buttonDM.y);
        this.selectionBox = new SelectionBox(this, lpButtonDM.x + buttonDM.w + 8, 
                                    lpButtonDM.y - menuHeight / 2 + buttonDM.h / 2, config);
        this.contentContainer.add(this.selectionBox);            

    }


    sourceButtonFindTheWordsClickListenerFunction(pfwDM, buttonDMClicked) {
        if (iteamWorkingModeInstance.getMode() !== ITeamModes.interaction) {
            return;
        }

        if (this.pressedSourceButtonDM === null) {
            this.pressedSourceButtonDM = buttonDMClicked;
            this.hideAllItemsExceptDestinationsPuzzleFindWords(pfwDM, buttonDMClicked);
        } else {
            let srcButtonDM = this.pressedSourceButtonDM;
            let dstButtonDM = buttonDMClicked;

            this.markSelectedItemsPuzzleFindWords(pfwDM, srcButtonDM, dstButtonDM);
            this.showAllItemsExceptAnsweredOnesPuzzleFindWords(pfwDM);
            this.pressedSourceButtonDM = null;
        }
    }


    markSelectedItemsPuzzleFindWords(pfwDM, srcButtonDM, dstButtonDM) {
        let srcRowIndex = -1;
        let srcColIndex = -1;
        let dstRowIndex = -1;
        let dstColIndex = -1;

        pfwDM.buttons.map((rowArr, rowIndex) => {
            rowArr.map((buttonDMTmp, colIndex) => {
                if (buttonDMTmp === srcButtonDM) {
                    srcRowIndex = rowIndex;
                    srcColIndex = colIndex;
                }

                if (buttonDMTmp === dstButtonDM) {
                    dstRowIndex = rowIndex;
                    dstColIndex = colIndex;
                }
                return null;
            });
            if (srcRowIndex > -1 && srcColIndex > -1 
                && dstRowIndex > -1 && dstColIndex > -1) {
                return null;
            }
            return null;
        });

        if (srcRowIndex === -1 || srcColIndex === -1 || dstRowIndex === -1 || dstColIndex === -1) {
            // source or destination not found in the puzzle, return immediately
            return;
        }

        if (srcRowIndex === dstRowIndex && srcColIndex === dstColIndex) {
            // if source and destinations are the same then return immediately
            return;
        }

        if (srcColIndex > dstColIndex) {
            let tmp = srcButtonDM;
            srcButtonDM = dstButtonDM;
            dstButtonDM = tmp;

            tmp = srcColIndex;
            srcColIndex = dstColIndex;
            dstColIndex = tmp;
        }


        if (srcRowIndex > dstRowIndex) {
            let tmp = srcButtonDM;
            srcButtonDM = dstButtonDM;
            dstButtonDM = tmp;

            tmp = srcRowIndex;
            srcRowIndex = dstRowIndex;
            dstRowIndex = tmp;
        }

        let itemWidth = pfwDM.w / pfwDM.columns;
        let itemHeight = pfwDM.h / pfwDM.rows;
        let selectedAreaUpperLeftCornerX = itemWidth * srcColIndex;
        let selectedAreaUpperLeftCornerY = itemHeight * srcRowIndex;
        let selectedAreaWidth = (dstColIndex - srcColIndex + 1) * itemWidth - pfwDM.margin;
        let selectedAreaHeight = (dstRowIndex - srcRowIndex + 1) * itemHeight - pfwDM.margin;


        let theSameAnswerIndex = -1;
        srcButtonDM.currentAnswers.map((answerTmp, index) => {  // detect the same answer
            if (answerTmp.srcRow === srcRowIndex && answerTmp.srcCol === srcColIndex &&
                    answerTmp.dstRow === dstRowIndex && answerTmp.dstCol === dstColIndex) {
                theSameAnswerIndex = index;
            }
            return null;
        });

        if (theSameAnswerIndex > -1) {
            // remove the same answer from answer list
            if (srcButtonDM.currentAnswers[theSameAnswerIndex].visualSprite !== null) {
                srcButtonDM.currentAnswers[theSameAnswerIndex].visualSprite.destroy();
            }
            srcButtonDM.currentAnswers.splice(theSameAnswerIndex, 1);
            return; // immediately return
        }

        // detect overlaps on the same row and the new one kills the old ones
        if (srcRowIndex === dstRowIndex) { // the row is the same, find other answered items on the same row, within the all column range
            for (let tmpColIndex = 0; tmpColIndex <= dstColIndex; tmpColIndex++) {
                

                let btnDMTmp = pfwDM.buttons[srcRowIndex][tmpColIndex];
                let btnsToBeRemoved = []; 
                btnDMTmp.currentAnswers.map(item => { // detect overlapping answers and push them on btnsToBeRemoved array
                    if (item.srcRow !== item.dstRow) {
                        return null;
                    }

                    let itemCols = [];
                    for (let i = item.srcCol; i <= item.dstCol; i++) {
                        itemCols.push(i);
                    }

                    let candidateCols = [];
                    for (let i = srcColIndex; i <= dstColIndex; i++) {
                        candidateCols.push(i);
                    }

                    itemCols.map(prevCol => {
                        candidateCols.map(candidateCol => {
                            if (prevCol === candidateCol) {
                                if (btnsToBeRemoved.indexOf(btnDMTmp) === -1) {
                                    btnsToBeRemoved.push(btnDMTmp);
                                }
                            }
                            return null;
                        });
                        return null;
                    });
                    return null;
                });


                // delete all horizontal overlapping answers
                btnsToBeRemoved.map(btnDMTmp => {
                    let indexToBeRemoved = -1;
                    btnDMTmp.currentAnswers.map((item, index) => {
                        if (item.srcRow === item.dstRow) {
                            indexToBeRemoved = index;
                        }
                        return null;
                    });
    
                    if (indexToBeRemoved > -1) {
                        if (btnDMTmp.currentAnswers[indexToBeRemoved].visualSprite !== null) {
                            btnDMTmp.currentAnswers[indexToBeRemoved].visualSprite.destroy();
                        }
                        btnDMTmp.currentAnswers.splice(indexToBeRemoved, 1);
                    }
                    return null;
                });

            }
        }


        // detect overlaps on the same col and the new one kills the old ones
        if (srcColIndex === dstColIndex) { // the column is the same, find other answered items on the same column, within the all row range
            for (let tmpRowIndex = 0; tmpRowIndex <= dstRowIndex; tmpRowIndex++) {
                

                let btnDMTmp = pfwDM.buttons[tmpRowIndex][srcColIndex];
                let btnsToBeRemoved = []; 
                btnDMTmp.currentAnswers.map(item => { // detect overlapping answers ans push them on btnsToBeRemoved array
                    if (item.srcCol !== item.dstCol) {
                        return null;
                    }

                    let itemRows = [];
                    for (let i = item.srcRow; i <= item.dstRow; i++) {
                        itemRows.push(i);
                    }

                    let candidateRows = [];
                    for (let i = srcRowIndex; i <= dstRowIndex; i++) {
                        candidateRows.push(i);
                    }

                    itemRows.map(prevRow => {
                        candidateRows.map(candidateRow => {
                            if (prevRow === candidateRow) {
                                if (btnsToBeRemoved.indexOf(btnDMTmp) === -1) {
                                    btnsToBeRemoved.push(btnDMTmp);
                                }
                            }
                            return null;
                        });
                        return null;
                    });
                    return null;
                });


                // delete all vertical overlapping answers
                btnsToBeRemoved.map(btnDMTmp => {
                    let indexToBeRemoved = -1;
                    btnDMTmp.currentAnswers.map((item, index) => {
                        if (item.srcCol === item.dstCol) {
                            indexToBeRemoved = index;
                        }
                        return null;
                    });
    
                    if (indexToBeRemoved > -1) {
                        if (btnDMTmp.currentAnswers[indexToBeRemoved].visualSprite !== null) {
                            btnDMTmp.currentAnswers[indexToBeRemoved].visualSprite.destroy();
                        }
                        btnDMTmp.currentAnswers.splice(indexToBeRemoved, 1);
                    }
                    return null;      
                });

            }
        }





        let colorcode = iteamColorModelInstance.getColor().colorcode;
        let spriteName = 'spritePFW' + uuidv4();
        this.createRoundedRectangleFillObjectInTexture(spriteName, selectedAreaWidth, selectedAreaHeight, colorcode, 6, 8);
        let spritePFW = this.add.sprite(selectedAreaUpperLeftCornerX, selectedAreaUpperLeftCornerY, spriteName).setOrigin(0);


        let newAnswer = {
            srcRow: srcRowIndex,
            srcCol: srcColIndex,
            dstRow: dstRowIndex,
            dstCol: dstColIndex,
            visualSprite: spritePFW,
            visualResultIndicator: null,
            isAnswerShown: false
        };



        srcButtonDM.currentAnswers.push(newAnswer);
        pfwDM.visualContainer.add(spritePFW);

    }

    showAllItemsExceptAnsweredOnesPuzzleFindWords(pfwDM) {
        let numRows = pfwDM.rows;
        let numCols = pfwDM.columns;
        let answeredButtons = [];
        for (let row = 0; row < numRows; row++) {
            for (let col = 0; col < numCols; col++) {
                let buttonCurrentDM = pfwDM.buttons[row][col];
                buttonCurrentDM.visualButton.fillColor = 0xff0000;
                buttonCurrentDM.visualButton.alpha = 0.6;
                buttonCurrentDM.visualButton.angle = 0;
                buttonCurrentDM.visualButton.setVisible(true);

                let tweenSrc = buttonCurrentDM.visualButton.data.get('tweenSrc');
                let tweenDst = buttonCurrentDM.visualButton.data.get('tweenDst');
                if (tweenSrc !== undefined && tweenSrc !== null) {
                    tweenSrc.restart();
                }
                if (tweenDst !== undefined && tweenDst !== null) {
                    tweenDst.pause();
                }


                buttonCurrentDM.currentAnswers.forEach((item, index) => {
                    if (item.srcRow === item.dstRow) {
                        for (let col = item.srcCol; col <= item.dstCol; col++) {
                            let answeredButtonDMCandidate = pfwDM.buttons[item.srcRow][col];
                            if (answeredButtons.indexOf(answeredButtonDMCandidate) === -1) {
                                answeredButtons.push(answeredButtonDMCandidate);
                            }
                        }
                    } else if (item.srcCol === item.dstCol) {
                        for (let row = item.srcRow; row <= item.dstRow; row++) {
                            let answeredButtonDMCandidate = pfwDM.buttons[row][item.srcCol];
                            if (answeredButtons.indexOf(answeredButtonDMCandidate) === -1) {
                                answeredButtons.push(answeredButtonDMCandidate);
                            }
                        }
                    }
                });
            }
        }


        answeredButtons.forEach(buttonDMAnswered => {
            let tweenSrc = buttonDMAnswered.visualButton.data.get('tweenSrc');
            let tweenDst = buttonDMAnswered.visualButton.data.get('tweenDst');
            if (tweenSrc !== undefined && tweenSrc !== null) {
                tweenSrc.pause();
            }
            if (tweenDst !== undefined && tweenDst !== null) {
                tweenDst.pause();
            }
            buttonDMAnswered.visualButton.fillColor = 0xff0000;
            buttonDMAnswered.visualButton.alpha = 0.0001;
            buttonDMAnswered.visualButton.angle = 0;
            buttonDMAnswered.visualButton.setVisible(true);
        });

    }


    hideAllItemsExceptDestinationsPuzzleFindWords(pfwDM, buttonDM) {
        let foundRowIndex = -1;
        let foundColIndex = -1;
        pfwDM.buttons.map((rowArr, rowIndex) => {
            rowArr.map((buttonTmp, colIndex) => {
                if (buttonDM === buttonTmp) {
                    foundRowIndex = rowIndex;
                    foundColIndex = colIndex;
                }
                return null;
            });
            if (foundRowIndex > -1 && foundColIndex > -1) {
                return null; // useless???
            }
            return null;
        });


        pfwDM.buttons.forEach((rowArr, rowIndex) => {
            rowArr.forEach((buttonTmp, colIndex) => {
                let tweenSrc = buttonTmp.visualButton.data.get('tweenSrc');
                let tweenDst = buttonTmp.visualButton.data.get('tweenDst');
                if (buttonDM === buttonTmp) {
                    buttonTmp.visualButton.setVisible(true);
                    buttonTmp.visualButton.fillColor = 0x0000ff;
                    buttonTmp.visualButton.alpha = 0.6;
                    if (tweenSrc !== undefined && tweenSrc !== null) {
                        tweenSrc.pause();
                    }                    
                    if (tweenDst !== undefined && tweenDst !== null) {
                        tweenDst.pause();
                    }

                    return null;
                }


                if ((rowIndex !== foundRowIndex && colIndex !== foundColIndex)
                    && rowIndex !== foundRowIndex && colIndex !== foundColIndex) {
                        /*
                         * Diagonal selections will be supported later
                         *
                        let deltaRow = Math.abs(rowIndex - foundRowIndex);
                        let deltaCol = Math.abs(colIndex - foundColIndex);
                        if (deltaRow === deltaCol) {
                            buttonTmp.visualButton.setVisible(true);
                            buttonTmp.visualButton.fillColor = 0xff0000;
                            buttonTmp.visualButton.alpha = 0.6;
                            if (tweenSrc !== undefined && tweenSrc !== null) {
                                tweenSrc.pause();
                            }                    
                            if (tweenDst !== undefined && tweenDst !== null) {
                                tweenDst.restart();
                            }
        
                        } else {
                            buttonTmp.visualButton.fillColor = 0xff0000;
                            buttonTmp.visualButton.setVisible(false);
                        }*/

                        buttonTmp.visualButton.fillColor = 0xff0000;
                        buttonTmp.visualButton.setVisible(false);

                } else {
                    buttonTmp.visualButton.setVisible(true);
                    buttonTmp.visualButton.fillColor = 0xff0000;
                    buttonTmp.visualButton.alpha = 0.6;

                    if (tweenSrc !== undefined && tweenSrc !== null) {
                        tweenSrc.pause();
                    }
                    if (tweenDst !== undefined && tweenDst !== null) {
                        // tweenDst.resetTweenData();
                        tweenDst.restart();
                    }
                }
            });
        });

    }


    generateSelectionBoxItemsConfig(questionDM) {
        let alternateValuesArr = questionDM.alternateValues;
        let alternateLabelsArr = questionDM.alternateLabels;
        let items = [];

        if (alternateValuesArr.length !== alternateLabelsArr.length) {
            let item = {
                text: 'error v != l',
                value: 'error',
                itemclickfn: () => this.selectionBoxItemClickHandler(questionDM, 'error')
            };

            items.push(item);
            return items;
        }

        alternateValuesArr.map((value, index) => {
            let labelText = alternateLabelsArr[index];

            let item = {
                text: labelText,
                value: value,
                itemclickfn: () => this.selectionBoxItemClickHandler(questionDM, value)
            };

            items.push(item);
            return null;
        });

        return items;
    }

    destinationButtonClickListenerFunction(exerciseDM, buttonDMDestination) {
        if (this.pressedSourceButtonDM === null) { // if there is no pressed source button, then exit.
            return;
        }

        let questionDM = this.findQuestionDMOfButtonDM(exerciseDM, this.pressedSourceButtonDM);

        if (this.pressedSourceButtonDM.type === buttonDMDestination.TYPE_SOURCE) {
            if (questionDM.type === 'match_and_write_to_a_box' || questionDM.type === 'match_and_write_to_a_group_box') {
                this.matchAndWriteToABoxDstButtonClickHelper(exerciseDM, buttonDMDestination);
                this.hideAllDestinationButtonsInExercise(exerciseDM);
                this.showAllSourceButtonsInExercise(exerciseDM, true);
            } else if (questionDM.type === 'match_and_draw_line_horizontal') {
                this.matchHorizontalDstButtonClickHelper(exerciseDM, buttonDMDestination);
                this.hideAllDestinationButtonsInExercise(exerciseDM);
                this.showAllSourceButtonsInExercise(exerciseDM, true);
            } else if (questionDM.type === 'match_and_draw_line_vertical') {
                this.matchVerticalDstButtonClickHelper(exerciseDM, buttonDMDestination);
                this.hideAllDestinationButtonsInExercise(exerciseDM);
                this.showAllSourceButtonsInExercise(exerciseDM, true);
            } else if (questionDM.type === 'match_words_to_make_sentence') {
                let exitReason = this.matchHorizontalDstButtonChainClickHelper(exerciseDM, buttonDMDestination);
                if (exitReason === 'the_same_is_selected_remove_the_selection') {
                    // the selection is already removed by matchHorizontalDstButtonChainClickHelper() and answers are freed
                    this.pressedSourceButtonDM = null;
                    this.hideAllDestinationButtonsInExercise(exerciseDM);
                    this.showAllSourceButtonsInExercise(exerciseDM, true);
                } else {
                    this.pressedSourceButtonDM = buttonDMDestination; // switch to new source button
                    this.hideOtherSourceLikeDestinationButtonsInExerciseExceptThePressedOne(exerciseDM, this.pressedSourceButtonDM);
                    this.showNextBtnsDestinationButtonsInExercise(exerciseDM, this.pressedSourceButtonDM);
                }
            }    
        } else { // the question type has to be "match_words_to_make_sentence" and the clicked source button is actually a destination behaving like source
            this.matchHorizontalDstButtonChainClickHelper(exerciseDM, buttonDMDestination);
            this.hideAllDestinationButtonsInExercise(exerciseDM);
            if (buttonDMDestination.nextbtns !== null) {
                // intermediate destination button is clicked, behave like a source button.
                this.pressedSourceButtonDM = buttonDMDestination; // switch the pressed source buttonDM
                this.hideOtherSourceLikeDestinationButtonsInExerciseExceptThePressedOne(exerciseDM, this.pressedSourceButtonDM);
                this.showNextBtnsDestinationButtonsInExercise(exerciseDM, this.pressedSourceButtonDM);
            } else {
                // the last destination button is clicked, that's all folks, finalize.
                this.pressedSourceButtonDM = null;
                this.hideAllDestinationButtonsInExercise(exerciseDM);
                this.showAllSourceButtonsInExercise(exerciseDM, true);
            }

        }
 
        
    }

    submitTextBoxValue(exerciseDM, buttonDM, submittedValue) {
        let _createTextObject = this.createTextObject.bind(this);
        let _contentContainer = this.contentContainer;
        let _showAllSourceButtonsInExercise = this.showAllSourceButtonsInExercise.bind(this);

        if (buttonDM.currentAnswers.length > 0) {
            buttonDM.currentAnswers = [];
        }
        if (submittedValue.length > 0) {
            buttonDM.currentAnswers.push(normalizeUserInput(submittedValue));
        }

        exerciseDM.questions.forEach(questionDMTmp => {

            if (questionDMTmp.type !== 'fill_in_the_blanks'
                && questionDMTmp.type !== 'fill_in_the_blanks_grouping'
                && questionDMTmp.type !== 'crossword_puzzle') {
                return null;
            }

            if (questionDMTmp.isAnswerShown) {
                return null;
            }

            questionDMTmp.buttons.forEach(buttonDMTmp => {
                if (questionDMTmp.type === 'fill_in_the_blanks'
                    || questionDMTmp.type === 'fill_in_the_blanks_grouping') {
                    // first remove text object
                    let text = buttonDMTmp.visualButton.data.get('textObject');
                    if (text !== undefined) {
                        buttonDMTmp.visualButton.data.remove('textObject');
                        text.destroy();
                    }

                    let colorcode = iteamColorModelInstance.getColor().colorcode;
                    if (buttonDMTmp.currentAnswers.length > 0) {
                        let answer = buttonDMTmp.currentAnswers[0];

                        // then create text object and add it to container
                        let lpButtonDMTmp = this.calculateLocalXYFromGlobalXY(exerciseDM, buttonDMTmp.x, buttonDMTmp.y);

                        text = _createTextObject(lpButtonDMTmp.x,
                            lpButtonDMTmp.y,
                            buttonDMTmp.w, buttonDMTmp.h,
                            answer, buttonDMTmp.align, exerciseDM.config.fontSize, colorcode, 'Arial', buttonDMTmp.rotation);

                        buttonDMTmp.visualButton.data.set('textObject', text);
                        _contentContainer.add(text);
                    }

                } else if (questionDMTmp.type === 'crossword_puzzle') {

                    if (buttonDMTmp.currentAnswers.length === 0) {
                        // first remove old written items
                        for (let i = 0; i < buttonDMTmp.labelModels.length; i++) {
                            let labelDM = buttonDMTmp.labelModels[i];
                            let text = labelDM.textObject;
                            if (text !== undefined && text !== null) {
                                text.destroy();
                            }
                        }
                        return null;
                    }

                    let answer = buttonDMTmp.currentAnswers[0];
                    let tmpAnswer = answer.replace(/ /g, '');


                    for (let i = 0; i < tmpAnswer.length && i < buttonDMTmp.labelModels.length; i++) {
                        let labelDM = buttonDMTmp.labelModels[i];
                        if (labelDM.skip) {
                            continue;
                        }

                        let currentChar = tmpAnswer.charAt(i);
                        if (buttonDMTmp.displayCase === 'uppercase') {
                            currentChar = currentChar.toUpperCase();
                        } else if (buttonDMTmp.displayCase === 'lowercase') {
                            currentChar = currentChar.toLowerCase();
                        }


                        let text = labelDM.textObject;
                        if (text !== undefined && text !== null) {
                            text.destroy();
                        }

                        let lp = this.calculateLocalXYFromGlobalXY(exerciseDM, labelDM.x, labelDM.y);
                        let colorcode = iteamColorModelInstance.getColor().colorcode;
                        text = _createTextObject(lp.x, lp.y,
                            labelDM.w, labelDM.h, currentChar, 'center', exerciseDM.config.fontSize, colorcode);
                        _contentContainer.add(text);

                        labelDM.textObject = text;
                    }



                }
            });

            _showAllSourceButtonsInExercise(exerciseDM, true);
        });

    }


    requestTextBox(exerciseDM, buttonDM) {

        // first find existing VirtualKeyboardScene instance and remove it from scene manager
        this.game.scene.scenes.forEach(sceneIterator => {
            if (sceneIterator instanceof VirtualKeyboardScene) {
                this.game.scene.remove('VirtualKeyboardScene');
                return;
            }
        });

        let initialValue = '';

        if (buttonDM.currentAnswers.length > 0) {
            let answer = buttonDM.currentAnswers[0];
            initialValue = answer;
        }



        // create a new instance of VirtualKeyboardScene and add it to scene manager.
        let virtualKeyboardScene = new VirtualKeyboardScene({
            config: this.config,
            exerciseDM: exerciseDM,
            buttonDM: buttonDM,
            initialValue: initialValue,
            inputSubmitHandler: this.submitTextBoxValue
        });

        this.game.scene.add('VirtualKeyboardScene', virtualKeyboardScene);

        this.scene.launch('VirtualKeyboardScene');

        this.resizeHandler();

    }


    // matchHorizontalDstButtonChainClickHelper() is used for match_words_to_make_sentence
    matchHorizontalDstButtonChainClickHelper(exerciseDM, buttonDMDestination) {
        let exitReason = null;
        if (this.pressedSourceButtonDM === null) {
            return exitReason;
        }

        let foundSrcButtonDM = this.followTheAnsweredChainAndFindTheSourceButton(exerciseDM, buttonDMDestination);

        if (foundSrcButtonDM !== null && foundSrcButtonDM === this.pressedSourceButtonDM) {
            // the same source button is found and has an answer (because it is found as a source of answered chain), 
            //      only remove whole answer of chains and exit 
            //      follow the chain of destinations to make all answers free
            this.followTheAnsweredChainAndMakeAllAnswersFree(exerciseDM, foundSrcButtonDM);
            exitReason = 'the_same_is_selected_remove_the_selection';
            return exitReason;
        } else if (foundSrcButtonDM !== null) {
            // different source button is found, but what about itself, if itself also has already answers the free them also.

            if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
                // the current source button itself has answered chain. Clear it first.
                this.followTheAnsweredChainAndMakeAllAnswersFree(exerciseDM, this.pressedSourceButtonDM);
            }

            // different source button is found, then remove all the chain of answers of found source.
            this.followTheAnsweredChainAndMakeAllAnswersFree(exerciseDM, foundSrcButtonDM);
        }




        // then continue with the new answer setting
        // make new selection

        let spriteMADHL = this.pressedSourceButtonDM.visualButton.data.get('visualSprite');

        if (spriteMADHL !== undefined) { // first remove previous visualSprite
            let key = spriteMADHL.key;
            this.pressedSourceButtonDM.visualButton.data.remove('visualSprite');
            spriteMADHL.destroy();
            if (this.textures.exists(key)) {
                this.textures.remove(key);
            }
        }

        let lpPressedSourceButtonDM = this.calculateLocalXYFromGlobalXY(exerciseDM, this.pressedSourceButtonDM.x, this.pressedSourceButtonDM.y);
        let lpButtonDMDestination = this.calculateLocalXYFromGlobalXY(exerciseDM, buttonDMDestination.x, buttonDMDestination.y);

        let x1 = lpPressedSourceButtonDM.x + this.pressedSourceButtonDM.w;
        let x2 = lpButtonDMDestination.x;

        let y1 = lpPressedSourceButtonDM.y;
        let y2 = lpButtonDMDestination.y;

        let srcH = this.pressedSourceButtonDM.h;
        let dstH = buttonDMDestination.h;


        if (x1 > x2) {
            x1 = lpButtonDMDestination.x + buttonDMDestination.w;
            x2 = lpPressedSourceButtonDM.x;

            srcH = buttonDMDestination.h;
            dstH = this.pressedSourceButtonDM.h;

            y1 = lpButtonDMDestination.y;
            y2 = lpPressedSourceButtonDM.y;
        }


        let spriteName = 'spriteMADHL' + uuidv4();
        let thickness = exerciseDM.config.drawingLineThickness;
        let colorcode = iteamColorModelInstance.getColor().colorcode;
        this.createMatchLineHorizontalObjectInTexture(spriteName,
                                                        x1, y1, x2, y2,
                                                        srcH, dstH,
                                                        thickness, colorcode);

        let x = x1;
        let y = y1;

        if (x1 > x2) {
            x = x2;
        } else {
            x = x1;
        }


        if (y1 > y2) {
            y = y2;
        } else {
            y = y1;
        }

        spriteMADHL = this.add.sprite(x, y, spriteName).setOrigin(0);

        this.contentContainer.add(spriteMADHL);
        this.pressedSourceButtonDM.visualButton.data.set('visualSprite', spriteMADHL);
        if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
            this.pressedSourceButtonDM.currentAnswers = [];
        }
        this.pressedSourceButtonDM.currentAnswers.push(buttonDMDestination.value);


        this.pressedSourceButtonDM = null;
        
        return exitReason;
    }

    // followTheAnsweredChainAndMakeAllAnswersFree() is a recursion function and 
    //      exit condition is there is no answered item is reached
    followTheAnsweredChainAndMakeAllAnswersFree(exerciseDM, currentButtonDM) {
        if (currentButtonDM === null || currentButtonDM.currentAnswers.length === 0) {
            return;
        }

        // free the current answer of source like destination button
        let currentAnswer = currentButtonDM.currentAnswers[0];
        currentButtonDM.currentAnswers = [];

        let spriteMADHL = currentButtonDM.visualButton.data.get('visualSprite');
        if (spriteMADHL !== undefined) {
            currentButtonDM.visualButton.data.remove('visualSprite');
            spriteMADHL.destroy();
        }

        exerciseDM.destinationButtons.forEach(destinationDMTmp => {
            if (destinationDMTmp.value === currentAnswer) {
                this.followTheAnsweredChainAndMakeAllAnswersFree(exerciseDM, destinationDMTmp);
            }
        });
    }

    followTheAnsweredChainAndFindTheSourceButton(exerciseDM, buttonDMDestination) {
        let foundDstButton = null;
        exerciseDM.destinationButtons.forEach(buttonDMTmp => {
            if (foundDstButton === null && buttonDMTmp.currentAnswers.length > 0 && buttonDMTmp.currentAnswers[0] === buttonDMDestination.value) {
                // if we found a destination button (buttonDMTmp) which has an answer 
                //      equal to current clicked destination button (buttonDMDestination)
                foundDstButton = buttonDMTmp;
            }
        });

        let foundSrcButton = null;
        if (foundDstButton === null) {
            // not found, so the leftmost destination button may be found, then check the source buttons
            exerciseDM.questions.forEach(questionDMTmp => {
                if (questionDMTmp.type !== 'match_words_to_make_sentence') {
                    return; // skip non match_words_to_make_sentence questions
                }
    
                // now on a match_words_to_make_sentence type question, lets iterate source buttons
                questionDMTmp.buttons.forEach(buttonDMTmpSrc => {
                    if (buttonDMTmpSrc.currentAnswers.length === 0) {
                        return; // non answered source buttons are skipped
                    }

                    // continue with answered source buttons.
                    if (foundSrcButton === null && buttonDMTmpSrc.currentAnswers.length > 0 && buttonDMTmpSrc.currentAnswers[0] === buttonDMDestination.value) {
                        foundSrcButton = buttonDMTmpSrc;
                    }
                });
            });

            return foundSrcButton;
        } else { 
            // a destination button is found where the answer is equal to the given one
            // then we must continue to search the previous one
            foundSrcButton = this.followTheAnsweredChainAndFindTheSourceButton(exerciseDM, foundDstButton);
            return foundSrcButton;
        }
    }

    // matchHorizontalDstButtonClickHelper() is used for match_and_draw_line_horizontal
    matchHorizontalDstButtonClickHelper(exerciseDM, buttonDMDestination) {

        // draw line to source button's target location and prepare cross for destination
        exerciseDM.destinationButtons.map(buttonDMTmp => {
            if (buttonDMTmp === buttonDMDestination) { 
                // first check if the pressed source is already selected to make it free
                if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
                    let currentAnswer = this.pressedSourceButtonDM.currentAnswers[0];
                    if (currentAnswer === buttonDMDestination.value) {
                        // the same is selected make it free and exit
                        this.pressedSourceButtonDM.currentAnswers = [];
                        let spriteMADHL = this.pressedSourceButtonDM.visualButton.data.get('visualSprite');
                        if (spriteMADHL !== undefined) {
                            this.pressedSourceButtonDM.visualButton.data.remove('visualSprite');
                            spriteMADHL.destroy();
                        }
                        return null;
                    }
                }


                let spriteMADHL = this.pressedSourceButtonDM.visualButton.data.get('visualSprite');

                if (spriteMADHL !== undefined) { // first remove previous visualSprite
                    let key = spriteMADHL.key;
                    this.pressedSourceButtonDM.visualButton.data.remove('visualSprite');
                    spriteMADHL.destroy();
                    if (this.textures.exists(key)) {
                        this.textures.remove(key);
                    }
                }

                let lpPressedSourceButtonDM = this.calculateLocalXYFromGlobalXY(exerciseDM, this.pressedSourceButtonDM.x, this.pressedSourceButtonDM.y);
                let lpButtonDMDestination = this.calculateLocalXYFromGlobalXY(exerciseDM, buttonDMDestination.x, buttonDMDestination.y);
        
                let x1 = lpPressedSourceButtonDM.x + this.pressedSourceButtonDM.w;
                let x2 = lpButtonDMDestination.x;

                let y1 = lpPressedSourceButtonDM.y;
                let y2 = lpButtonDMDestination.y;

                let srcH = this.pressedSourceButtonDM.h;
                let dstH = buttonDMDestination.h;


                if (x1 > x2) {
                    x1 = lpButtonDMDestination.x + buttonDMDestination.w;
                    x2 = lpPressedSourceButtonDM.x;

                    srcH = buttonDMDestination.h;
                    dstH = this.pressedSourceButtonDM.h;

                    y1 = lpButtonDMDestination.y;
                    y2 = lpPressedSourceButtonDM.y;
                }


                let spriteName = 'spriteMADHL' + uuidv4();
                let thickness = exerciseDM.config.drawingLineThickness;
                let colorcode = iteamColorModelInstance.getColor().colorcode;
                this.createMatchLineHorizontalObjectInTexture(spriteName,
                                                                x1, y1, x2, y2,
                                                                srcH, dstH,
                                                                thickness, colorcode);

                let x = x1;
                let y = y1;

                if (x1 > x2) {
                    x = x2;
                } else {
                    x = x1;
                }


                if (y1 > y2) {
                    y = y2;
                } else {
                    y = y1;
                }

                spriteMADHL = this.add.sprite(x, y, spriteName).setOrigin(0);

                this.contentContainer.add(spriteMADHL);
                this.pressedSourceButtonDM.visualButton.data.set('visualSprite', spriteMADHL);
                if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
                    this.pressedSourceButtonDM.currentAnswers = [];
                }
                this.pressedSourceButtonDM.currentAnswers.push(buttonDMDestination.value);
            } 
            return null;
        });

        // remove sprite from source button's location if necessary (prevent duplicate usage)
        exerciseDM.questions.map(questionDMTmp => {
            if (questionDMTmp.type !== 'match_and_draw_line_horizontal') {
                return null;
            }

            questionDMTmp.buttons.map(buttonDMTmpSrc => {
                if (buttonDMTmpSrc.currentAnswers.length === 0) {
                    return null;
                }
                let currentAnswer = buttonDMTmpSrc.currentAnswers[0];

                if (currentAnswer === buttonDMDestination.value && buttonDMTmpSrc !== this.pressedSourceButtonDM) {
                    buttonDMTmpSrc.currentAnswers = [];
                    buttonDMTmpSrc.visualButton.setVisible(true);
                    let spriteMADHL = buttonDMTmpSrc.visualButton.data.get('visualSprite');
                    if (spriteMADHL !== undefined) {
                        buttonDMTmpSrc.visualButton.data.remove('visualSprite');
                        spriteMADHL.destroy();
                    }
                }
                return null;
            });
            return null;
        });

        this.pressedSourceButtonDM = null;
    }



    matchVerticalDstButtonClickHelper(exerciseDM, buttonDMDestination) {

        // drav line to source button's target location and prepare cross for destination
        exerciseDM.destinationButtons.map(buttonDMTmp => {
            if (buttonDMTmp === buttonDMDestination) {

                // first check if the selected one is already selected to make it free
                if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
                    let currentAnswer = this.pressedSourceButtonDM.currentAnswers[0];
                    if (currentAnswer === buttonDMDestination.value) {
                        // the same is selected make it free and exit
                        this.pressedSourceButtonDM.currentAnswers = [];
                        let spriteMADVL = this.pressedSourceButtonDM.visualButton.data.get('visualSprite');
                        if (spriteMADVL !== undefined) {
                            this.pressedSourceButtonDM.visualButton.data.remove('visualSprite');
                            spriteMADVL.destroy();
                        }
                        return null;
                    }
                }


                let spriteMADVL = this.pressedSourceButtonDM.visualButton.data.get('visualSprite');

                if (spriteMADVL !== undefined) { // first remove previous visualSprite
                    let key = spriteMADVL.key;
                    this.pressedSourceButtonDM.visualButton.data.remove('visualSprite');
                    spriteMADVL.destroy();
                    if (this.textures.exists(key)) {
                        this.textures.remove(key);
                    }
                }

                let lpPressedSourceButtonDM = this.calculateLocalXYFromGlobalXY(exerciseDM, this.pressedSourceButtonDM.x, this.pressedSourceButtonDM.y);
                let lpButtonDMDestination = this.calculateLocalXYFromGlobalXY(exerciseDM, buttonDMDestination.x, buttonDMDestination.y);

                let x1 = lpPressedSourceButtonDM.x;
                let x2 = lpButtonDMDestination.x;

                let y1 = lpPressedSourceButtonDM.y + this.pressedSourceButtonDM.h;
                let y2 = lpButtonDMDestination.y;

                let srcW = this.pressedSourceButtonDM.w;
                let dstW = buttonDMDestination.w;

                if (y1 > y2) {
                    x1 = lpButtonDMDestination.x;
                    x2 = lpPressedSourceButtonDM.x;
    
                    y1 = lpButtonDMDestination.y + buttonDMDestination.h;
                    y2 = lpPressedSourceButtonDM.y;
    
                    srcW = buttonDMDestination.w;
                    dstW = this.pressedSourceButtonDM.w;                        
                }

                let spriteName = 'spriteMADVL' + uuidv4(); // vertical line
                let thickness = exerciseDM.config.drawingLineThickness;
                let colorcode = iteamColorModelInstance.getColor().colorcode;
                this.createMatchLineVerticalObjectInTexture(spriteName,
                                                                x1, y1, x2, y2,
                                                                srcW, dstW,
                                                                thickness, colorcode);

                let x = x1;
                let y = y1;

                if (x1 > x2) {
                    x = x2;
                } else {
                    x = x1;
                }


                if (y1 > y2) {
                    y = y2;
                } else {
                    y = y1;
                }

                spriteMADVL = this.add.sprite(x, y, spriteName).setOrigin(0);

                this.contentContainer.add(spriteMADVL);
                this.pressedSourceButtonDM.visualButton.data.set('visualSprite', spriteMADVL);
                if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
                    this.pressedSourceButtonDM.currentAnswers = [];
                }
                this.pressedSourceButtonDM.currentAnswers.push(buttonDMDestination.value);
            } 
            return null;
        });

        // remove sprite from source button's location if necessary (prevent duplicate usage)
        exerciseDM.questions.map(questionDMTmp => {
            if (questionDMTmp.type !== 'match_and_draw_line_vertical') {
                return null;
            }

            questionDMTmp.buttons.map(buttonDMTmpSrc => {
                if (buttonDMTmpSrc.currentAnswers.length === 0) {
                    return null;
                }
                let currentAnswer = buttonDMTmpSrc.currentAnswers[0];

                if (currentAnswer === buttonDMDestination.value && buttonDMTmpSrc !== this.pressedSourceButtonDM) {
                    buttonDMTmpSrc.currentAnswers = [];
                    buttonDMTmpSrc.visualButton.setVisible(true);
                    let spriteMADVL = buttonDMTmpSrc.visualButton.data.get('visualSprite');
                    if (spriteMADVL !== undefined) {
                        buttonDMTmpSrc.visualButton.data.remove('visualSprite');
                        spriteMADVL.destroy();
                    }
                }
                return null;
            });
            return null;
        });

        this.pressedSourceButtonDM = null;
    }




    selectionBoxItemClickHandler(questionDM, value) {

        if (this.selectionBox === null || this.pressedSourceButtonDM === null) {
            return;
        }

        this.selectionBox.destroy();
        this.selectionBox = null;

        // first check if the selected one is already selected to make it free
        if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
            let currentAnswer = this.pressedSourceButtonDM.currentAnswers[0];
            if (currentAnswer === value) {
                // the same is selected make it free and exit
                this.pressedSourceButtonDM.currentAnswers = [];
                let text = this.pressedSourceButtonDM.visualButton.data.get('textObject');
                if (text !== undefined) {
                    this.pressedSourceButtonDM.visualButton.data.remove('textObject');
                    text.destroy();
                }
                this.pressedSourceButtonDM = null;
                this.showAllSourceButtonsInExercise(this.exerciseDM, true);
                return;
            }
        }


        let text = this.pressedSourceButtonDM.visualButton.data.get('textObject');

        if (text !== undefined) {
            this.pressedSourceButtonDM.visualButton.data.remove('textObject');
            text.destroy();
        }


        let fontFamily = 'Arial';
        if (questionDM.type === 'write_tick_or_cross') {
            fontFamily = 'wingdings2';
        }

        let fontSize = this.exerciseDM.config.fontSize;

        if (questionDM.type === 'write_tick_or_cross') {
            fontSize = this.exerciseDM.config.fontSize + 8;
        }


        let colorcode = iteamColorModelInstance.getColor().colorcode;
        let lpPressedSourceButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, this.pressedSourceButtonDM.x, this.pressedSourceButtonDM.y);
        text = this.createTextObject(lpPressedSourceButtonDM.x,
            lpPressedSourceButtonDM.y,
            this.pressedSourceButtonDM.w, this.pressedSourceButtonDM.h,
            value, this.pressedSourceButtonDM.align, fontSize, colorcode, fontFamily);

        text.angle = this.pressedSourceButtonDM.rotation;
        this.contentContainer.add(text);
        this.pressedSourceButtonDM.visualButton.data.set('textObject', text);

        if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
            this.pressedSourceButtonDM.currentAnswers = [];
        }
        this.pressedSourceButtonDM.currentAnswers.push(value);

        this.pressedSourceButtonDM = null;

        this.showAllSourceButtonsInExercise(this.exerciseDM, true);
    }

    matchAndWriteToABoxDstButtonClickHelper(exerciseDM, buttonDMDestination) {

        // write text to source button's target location and prepare cross for destination
        let allDestinationButtonDMs = [];
        exerciseDM.destinationButtons.forEach(buttonDMTmp => {
            allDestinationButtonDMs.push(buttonDMTmp); 
            if (buttonDMTmp !== buttonDMDestination) {
                return;
            }

            // first check if the selected one is already selected to make it free
            if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
                let currentAnswer = this.pressedSourceButtonDM.currentAnswers[0];
                if (currentAnswer === buttonDMDestination.value) {
                    // the same is selected make it free and exit
                    this.pressedSourceButtonDM.currentAnswers = [];
                    let text = this.pressedSourceButtonDM.visualButton.data.get('textObject');
                    if (text !== undefined) {
                        this.pressedSourceButtonDM.visualButton.data.remove('textObject');
                        text.destroy();
                    }
                    let spriteMAWTAB = buttonDMDestination.visualButton.data.get('visualSprite');
                    if (spriteMAWTAB !== undefined) {
                        buttonDMDestination.visualButton.data.remove('visualSprite');
                        let key = spriteMAWTAB.key;
                        spriteMAWTAB.destroy();
                        if (this.textures.exists(key)) {
                            this.textures.remove(key);
                        }
                    }
                    return;
                }
            }


            let spriteMAWTAB = buttonDMDestination.visualButton.data.get('visualSprite');
            if (spriteMAWTAB === undefined) {
                let spriteName = 'spriteMAWTAB' + uuidv4();
                let thickness = +exerciseDM.config.drawingLineThickness;
                let colorcode = iteamColorModelInstance.getColor().colorcode;
                this.createUsedDestinationCrossObjectInTexture(spriteName, 
                                buttonDMDestination.w, buttonDMDestination.h,
                                thickness, colorcode);
                let lpButtonDMDestination = this.calculateLocalXYFromGlobalXY(exerciseDM, buttonDMDestination.x, buttonDMDestination.y);
                spriteMAWTAB = this.add.sprite(lpButtonDMDestination.x,
                                lpButtonDMDestination.y, spriteName).setOrigin(0);
                buttonDMDestination.visualButton.data.set('visualSprite', spriteMAWTAB);
            }


            let text = this.pressedSourceButtonDM.visualButton.data.get('textObject');

            if (text !== undefined) {
                this.pressedSourceButtonDM.visualButton.data.remove('textObject');
                text.destroy();
            }
            
            let fontSize = exerciseDM.config.fontSize;
            let colorcode = iteamColorModelInstance.getColor().colorcode;
            let lpPressedSourceButtonDMTarget = this.calculateLocalXYFromGlobalXY(exerciseDM, this.pressedSourceButtonDM.targetx, this.pressedSourceButtonDM.targety);
            text = this.createTextObject(lpPressedSourceButtonDMTarget.x,
                        lpPressedSourceButtonDMTarget.y,
                        this.pressedSourceButtonDM.targetw, this.pressedSourceButtonDM.targeth,
                        buttonDMTmp.value, this.pressedSourceButtonDM.align, fontSize, colorcode, 'Arial', this.pressedSourceButtonDM.targetRotation);

            text.angle = this.pressedSourceButtonDM.targetRotation;
            this.contentContainer.add(text);
            this.pressedSourceButtonDM.visualButton.data.set('textObject', text);
            if (this.pressedSourceButtonDM.currentAnswers.length > 0) {
                this.pressedSourceButtonDM.currentAnswers = [];
            }
            this.pressedSourceButtonDM.currentAnswers.push(buttonDMDestination.value);
        });

        // remove text from source button's target location if necessary (prevent duplicate usage)
        let allSourceButtonDMs = [];
        exerciseDM.questions.forEach(questionDMTmp => {
            if (questionDMTmp.type !== 'match_and_write_to_a_box' && questionDMTmp.type !== 'match_and_write_to_a_group_box') {
                return;
            }

            questionDMTmp.buttons.forEach(buttonDMTmpSrc => {
                allSourceButtonDMs.push(buttonDMTmpSrc);
                if (buttonDMTmpSrc.currentAnswers.length === 0) {
                    return;
                }
                let currentAnswer = buttonDMTmpSrc.currentAnswers[0];

                if (currentAnswer === buttonDMDestination.value && buttonDMTmpSrc !== this.pressedSourceButtonDM) {
                    buttonDMTmpSrc.currentAnswers = [];
                    buttonDMTmpSrc.visualButton.setVisible(true);
                    let text = buttonDMTmpSrc.visualButton.data.get('textObject');
                    if (text !== undefined) {
                        buttonDMTmpSrc.visualButton.data.remove('textObject');
                        this.contentContainer.remove(text);
                        text.destroy();
                    }
                }
            });
        });


        // draw cross lines on used destinations
        let drawCanidates = [];
        
        allSourceButtonDMs.forEach(buttonDMSrcTmp => {
            if (buttonDMSrcTmp.currentAnswers.length === 0) { // skip unanswered
                return;
            }

            let currentAnswer = buttonDMSrcTmp.currentAnswers[0]; // get the first answer
            allDestinationButtonDMs.forEach(buttonDMDstTmp => {
                if (currentAnswer === buttonDMDstTmp.value) {
                    drawCanidates.push(buttonDMDstTmp);
                    let spriteMAWTAB = buttonDMDstTmp.visualButton.data.get('visualSprite');
                    this.contentContainer.add(spriteMAWTAB); // add the circle sprite to container
                }
            });
        });

        allDestinationButtonDMs.forEach(buttonDMDstTmp => {
            let used = false;
            drawCanidates.forEach(buttonDMUsedTmp => {
                if (buttonDMDstTmp === buttonDMUsedTmp) {
                    used = true;
                }
            });

            if (!used) {
                let spriteMAWTAB = buttonDMDstTmp.visualButton.data.get('visualSprite');
                if (spriteMAWTAB !== undefined) {
                    this.contentContainer.remove(spriteMAWTAB);
                    buttonDMDstTmp.visualButton.data.remove('visualSprite');
                    let key = spriteMAWTAB.key;
                    spriteMAWTAB.destroy();
                    if (this.textures.exists(key)) {
                        this.textures.remove(key);
                    }
                }
            }
        });

        this.pressedSourceButtonDM = null;
    }

    matchSrcButtonClickHelper(exerciseDM, buttonDM) {

        let questionDM = this.findQuestionDMOfButtonDM(exerciseDM, buttonDM);

        if (questionDM === null) {
            return; // strange, not expected
        }

        if (questionDM.type === 'match_and_write_to_a_box' 
            || questionDM.type === 'match_and_write_to_a_group_box'
            || questionDM.type === 'match_and_draw_line_horizontal'
            || questionDM.type === 'match_and_draw_line_vertical') {
                
            if (this.pressedSourceButtonDM === null) {
                this.pressedSourceButtonDM = buttonDM;
                this.hideOtherSourceButtonsInExerciseExceptThePressedOne(exerciseDM, buttonDM);
                this.showAllDestinationButtonsInExercise(exerciseDM);   
            } else {
                this.showAllSourceButtonsInExercise(exerciseDM, true);
                this.hideAllDestinationButtonsInExercise(exerciseDM);
                this.pressedSourceButtonDM = null;
            }    

        } else if (questionDM.type === 'write_t_or_f'
            || questionDM.type === 'write_y_or_n'
            || questionDM.type === 'write_true_or_false'
            || questionDM.type === 'write_tick_or_cross'
            || questionDM.type === 'write_from_selection_box'
            || questionDM.type === 'write_yes_or_no') {
            
            this.pressedSourceButtonDM = buttonDM;
            this.hideOtherSourceButtonsInExerciseExceptThePressedOne(exerciseDM, buttonDM);
            // show part is handled by selectionBoxItemClickHandler()
        } else if (questionDM.type === 'match_words_to_make_sentence') {

            if (this.pressedSourceButtonDM === null) {
                this.pressedSourceButtonDM = buttonDM;
                this.hideOtherSourceButtonsInExerciseExceptThePressedOne(exerciseDM, buttonDM);
                this.showNextBtnsDestinationButtonsInExercise(exerciseDM, buttonDM);   
            } else {
                this.showAllSourceButtonsInExercise(exerciseDM, true);
                this.hideAllDestinationButtonsInExercise(exerciseDM);
                this.pressedSourceButtonDM = null;
            }
            
        }
    }


    putATickIntoTheCorrectOneButtonClickHelper(exerciseDM, questionDM, buttonDM) {


        questionDM.buttons.map(buttonDMTmp => {
            let textObject = buttonDMTmp.visualButton.data.get('textObject');
            if (buttonDMTmp === buttonDM) {

                if (buttonDM.currentAnswers.length > 0) {
                    let currentAnswer = buttonDM.currentAnswers[0];
                    if (currentAnswer === 'true') {
                        buttonDM.currentAnswers = [];
                        if (textObject !== undefined) {
                            this.contentContainer.remove(textObject);
                            textObject.destroy();
                            buttonDMTmp.visualButton.data.remove('textObject');
                        }
                        return null;
                    }
                }

                textObject = buttonDMTmp.visualButton.data.get('textObject');

                let fontSize = exerciseDM.config.fontSize + 8;
                let colorcode = iteamColorModelInstance.getColor().colorcode;
                if (textObject === undefined) {
                    let lpButtonDM = this.calculateLocalXYFromGlobalXY(exerciseDM, buttonDM.x, buttonDM.y);
                    textObject = this.createTextObject(lpButtonDM.x, lpButtonDM.y,
                        buttonDM.w, buttonDM.h, 'P', 'center', fontSize, colorcode, 'wingdings2');
                    this.contentContainer.add(textObject); // add the circle sprite to container
                    buttonDM.visualButton.data.set('textObject', textObject);
                } else {
                    this.contentContainer.add(textObject);
                }
                buttonDMTmp.currentAnswers = [];
                buttonDMTmp.currentAnswers.push('true');
            } else {
                if (textObject !== undefined) {
                    this.contentContainer.remove(textObject); // remove the sprite from container
                    textObject.destroy();
                    buttonDMTmp.visualButton.data.remove('textObject');
                }
                buttonDMTmp.currentAnswers = [];
            }
            return null;
        });  
        
        let hasAtLeastOneAnswer = false;
        questionDM.buttons.map(buttonDMTmp => {
            if (buttonDMTmp.currentAnswers.length > 0) {
                hasAtLeastOneAnswer = true;
            }
            return null;
        });

        if (hasAtLeastOneAnswer) {
            questionDM.buttons.map(buttonDMTmp => {
                let tween = buttonDMTmp.visualButton.data.get('tween');
                if (tween !== undefined && tween !== null) {
                    tween.pause();
                }
                buttonDMTmp.visualButton.alpha = 0.0001; // if it is set to zero, then input hooks does not work
                return null;
            });    
        } else {
            questionDM.buttons.map(buttonDMTmp => {
                let tween = buttonDMTmp.visualButton.data.get('tween');
                if (tween !== undefined && tween !== null) {
                    tween.reset();
                    tween.restart();
                }
                return null;
            });
        }

        // reset all unanswered button's tweens to get them synchronized
        exerciseDM.questions.map(questionDMTmp => {
            questionDMTmp.buttons.map(buttonDMTmp => {
                let tween = buttonDMTmp.visualButton.data.get('tween');
                if (tween !== undefined) {
                    // tween.resetTweenData();
                    tween.reset();
                }
                return null;
            });
            return null;
        });
        

    }



    circleTheCorrectOneButtonClickHelper(exerciseDM, questionDM, buttonDM) {
        let margin = 5;     

        questionDM.buttons.map(buttonDMTmp => {
            let spriteCTCO = buttonDMTmp.visualButton.data.get('visualSprite');
            if (buttonDMTmp === buttonDM) {

                if (buttonDM.currentAnswers.length > 0) {
                    let currentAnswer = buttonDM.currentAnswers[0];
                    if (currentAnswer === 'true') {
                        buttonDM.currentAnswers = [];
                        if (spriteCTCO !== undefined) {
                            this.contentContainer.remove(spriteCTCO);
                            spriteCTCO.destroy();
                            buttonDMTmp.visualButton.data.remove('visualSprite');
                        }
                        return null;
                    }
                }

                if (spriteCTCO === undefined) {
                    let spriteName = 'spriteCTCO' + uuidv4();
                    let thickness = +exerciseDM.config.drawingLineThickness;
                    let colorcode = iteamColorModelInstance.getColor().colorcode;
                    this.createCircleObjectInTexture(spriteName, buttonDM.w, buttonDM.h, margin, thickness, colorcode);

                    let lp = this.calculateLocalXYFromGlobalXY(exerciseDM, buttonDM.x, buttonDM.y);
                    spriteCTCO = this.add.sprite(lp.x - margin, lp.y - margin, 
                                                        spriteName).setOrigin(0);
                    this.contentContainer.add(spriteCTCO); // add the circle sprite to container
                    buttonDM.visualButton.data.set('visualSprite', spriteCTCO);
                } else {
                    this.contentContainer.add(spriteCTCO);
                }
                buttonDMTmp.currentAnswers = [];
                buttonDMTmp.currentAnswers.push('true');
            } else {
                if (spriteCTCO !== undefined) {
                    this.contentContainer.remove(spriteCTCO); // remove the sprite from container
                    spriteCTCO.destroy();
                    buttonDMTmp.visualButton.data.remove('visualSprite');
                }
                buttonDMTmp.currentAnswers = [];
            }
            return null;
        });  
        
        let hasAtLeastOneAnswer = false;
        questionDM.buttons.map(buttonDMTmp => {
            if (buttonDMTmp.currentAnswers.length > 0) {
                hasAtLeastOneAnswer = true;
            }
            return null;
        });


        if (hasAtLeastOneAnswer) {
            questionDM.buttons.map(buttonDMTmp => {
                let tween = buttonDMTmp.visualButton.data.get('tween');
                if (tween !== undefined && tween !== null) {
                    tween.pause();
                }
                buttonDMTmp.visualButton.alpha = 0.0001; // if it is set to zero, then input hooks does not work
                return null;
            });    
        } else {
            questionDM.buttons.map(buttonDMTmp => {
                let tween = buttonDMTmp.visualButton.data.get('tween');
                if (tween !== undefined && tween !== null) {
                    tween.reset();
                    tween.restart();
                }
                return null;
            });
        }

        // reset all unanswered button's tweens to get them synchronized
        exerciseDM.questions.map(questionDMTmp => {
            questionDMTmp.buttons.map(buttonDMTmp => {
                let tween = buttonDMTmp.visualButton.data.get('tween');
                if (tween !== undefined && tween !== null) {
                    tween.reset();
                }
                return null;
            });
            return null;
        });

    }

    hideOtherSourceButtonsInExerciseExceptThePressedOne(exerciseDM, buttonDM) {
        if (buttonDM.type !== buttonDM.TYPE_SOURCE) {
            return;
        }

        exerciseDM.questions.map(questionDMTmp => {
            questionDMTmp.buttons.map(buttonDMTmp => {
                let tween = buttonDMTmp.visualButton.data.get('tween');
                if (tween !== undefined && tween !== null) {
                    tween.pause(); // pause all source button tweens
                }

                if (buttonDMTmp === buttonDM) {
                    buttonDMTmp.visualButton.alpha = 0.6;
                    buttonDMTmp.visualButton.setVisible(true);  // show clicked button
                } else {
                    buttonDMTmp.visualButton.setVisible(false);  // hide other buttons
                }
                return null;
            });
            return null;
        });
    }


    hideOtherSourceLikeDestinationButtonsInExerciseExceptThePressedOne(exerciseDM, buttonDM) {
        if (buttonDM.type !== buttonDM.TYPE_DESTINATION) {
            return;
        }

        exerciseDM.destinationButtons.forEach(buttonDMTmp => {
            let tween = buttonDMTmp.visualButton.data.get('tween');
            if (tween !== undefined && tween !== null) {
                tween.pause(); // pause all source like destination button tweens
            }

            if (buttonDMTmp === buttonDM && buttonDM.nextbtns !== null) {
                buttonDMTmp.visualButton.alpha = 0.6;
                buttonDMTmp.visualButton.rotation = 0;
                buttonDMTmp.visualButton.setVisible(true);  // show clicked button
            } else {
                buttonDMTmp.visualButton.setVisible(false);  // hide other buttons
            }
        });
    }


    showAllSourceButtonsInExercise(exerciseDM, keepAnsweredHidden = true) {
        exerciseDM.questions.forEach(questionDMTmp => {
            if (questionDMTmp.isAnswerShown) {
                return null;
            }

            questionDMTmp.buttons.forEach(buttonDMTmp => {
                if (!keepAnsweredHidden) {
                    buttonDMTmp.visualButton.setVisible(true);  // show clicked button

                    let tween = buttonDMTmp.visualButton.data.get('tween');
                    if (tween !== undefined && tween !== null) {
                        tween.restart(); // resume all source button tweens      
                    }
                } else {
                    if (buttonDMTmp.currentAnswers.length > 0) {
                        let tween = buttonDMTmp.visualButton.data.get('tween');
                        if (tween !== undefined && tween !== null) {
                            tween.pause(); // resume all source button tweens  
                        }
                        buttonDMTmp.visualButton.setVisible(true);
                        buttonDMTmp.visualButton.alpha = 0.0001;
                    } else {
                        buttonDMTmp.visualButton.setVisible(true);  // show clicked button
                        let tween = buttonDMTmp.visualButton.data.get('tween');
                        if (tween !== undefined && tween !== null) {
                            tween.restart(); // resume all source button tweens  
                        }
                    }
    
                }
            });
        });
    }

    showAllDestinationButtonsInExercise(exerciseDM) {
        exerciseDM.destinationButtons.map(buttonDMTmp => {
            buttonDMTmp.visualButton.setVisible(true);  // show destination button
            let tween = buttonDMTmp.visualButton.data.get('tween');
            tween.restart(); // resume all destination button tweens
            return null;
        });
    }

    hideAllDestinationButtonsInExercise(exerciseDM) {
        exerciseDM.destinationButtons.map(buttonDMTmp => {
            let tween = buttonDMTmp.visualButton.data.get('tween');
            tween.pause(); // pause all destination button tweens
            buttonDMTmp.visualButton.setVisible(false);  // hide destination button
            return null;
        });
    }


    isStringExistInArray(str, strArray) {
        for (let j=0; j<strArray.length; j++) {
            if (strArray[j].match(str)) return true;
        }
        return false;
    }

    showNextBtnsDestinationButtonsInExercise(exerciseDM, sourceBtnDM) {
        if (sourceBtnDM.id === null || sourceBtnDM.id === '' 
                    || sourceBtnDM.nextbtns === null || sourceBtnDM.nextbtns === '' 
                    || sourceBtnDM.nextbtnsarray === null || sourceBtnDM.nextbtnsarray.length === 0) {
            return;  // this sourceBtnDM is not a button of question type match words to make sentence, exit from method immediately
        }


        exerciseDM.destinationButtons.forEach(buttonDMTmp => {
            if (!this.isStringExistInArray(buttonDMTmp.id, sourceBtnDM.nextbtnsarray)) {
                return;
            }
            buttonDMTmp.visualButton.setVisible(true);  // show destination button
            let tween = buttonDMTmp.visualButton.data.get('tween');
            tween.restart(); // resume all destination button tweens
        });
    }


    findQuestionDMOfButtonDM(exerciseDM, buttonDM) {
        let questionDM = null;
        exerciseDM.questions.map(questionDMTmp => {
            questionDMTmp.buttons.map(buttonDMTmp => {
                if (buttonDMTmp === buttonDM) {
                    questionDM = questionDMTmp;
                }
                return null;
            });
            if (questionDM !== null) {
                return null; // question Data Model of source is found
            }
            return null;
        });
        return questionDM;
    }

    showAnswerOneByOneHandler() {

        /*
        alert('This is student\'s version of i-TEAM!\nAnswers are not shown to students.\nPlease, use teacher\'s version to see the answers.');
        return;
*/


        if (this.exerciseDM === null) {
            return;
        }

        let zoomInitX = this.exerciseDM.zoomInits[0].x;
        let zoomInitY = this.exerciseDM.zoomInits[0].y;
        let margin = 5;  


        let anAnswerIsShown = false;
        this.exerciseDM.questions.forEach((tmpQuestionDM) => {
            if (anAnswerIsShown) {
                return null; // something is shown in this map, skip rest of all
            }

            if (tmpQuestionDM.isAnswerShown) {
                return null; // skip this question
            }

            // now first answer not shown question is found

            let colorcode = ColorsDM.red.colorcode; // show answer default color

            tmpQuestionDM.buttons.forEach((tmpButtonDM, tmpButtonDMIndex) => {

                if (tmpButtonDM.visualResultIndicator !== null) {
                    let indicator = tmpButtonDM.visualResultIndicator;
                    this.contentContainer.remove(indicator);
                    indicator.destroy();
                    tmpButtonDM.visualResultIndicator = null;
                }

                if (tmpQuestionDM.type === 'circle_the_correct_one') { 



                    let visualSprite = tmpButtonDM.visualButton.data.get('visualSprite');
                    if (visualSprite !== undefined) {
                        visualSprite.destroy();
                        tmpButtonDM.visualButton.data.remove('visualSprite')
                    }

                    let correctAnswer = tmpButtonDM.correctAnswers[0];
                    if (correctAnswer === 'true') {
                        let spriteName = 'spriteCTCO' + uuidv4();
                        let thickness = this.exerciseDM.config.drawingLineThickness;
                        this.createCircleObjectInTexture(spriteName, tmpButtonDM.w, tmpButtonDM.h, margin, thickness, colorcode);
                        let lp = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpButtonDM.x, tmpButtonDM.y);
                        let spriteCTCO = this.add.sprite(lp.x - margin, lp.y - margin, 
                                                            spriteName).setOrigin(0);
                        this.contentContainer.add(spriteCTCO); // add the circle sprite to container
                        tmpButtonDM.visualButton.data.set('visualSprite', spriteCTCO);
                    }


                } else if (tmpQuestionDM.type === 'put_a_tick_into_the_correct_one') {

                    let fontSize = this.exerciseDM.config.fontSize + 8;

                    let textObject = tmpButtonDM.visualButton.data.get('textObject');
                    if (textObject !== undefined) {
                        textObject.destroy();
                        tmpButtonDM.visualButton.data.remove('textObject')
                    }

                    let correctAnswer = tmpButtonDM.correctAnswers[0];
                    if (correctAnswer === 'true') {
                        let lp = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpButtonDM.x, tmpButtonDM.y);
                        textObject = this.createTextObject(lp.x, lp.y,
                            tmpButtonDM.w, tmpButtonDM.h, 'P', 'center', fontSize, colorcode, 'wingdings2');
                        textObject.angle = tmpButtonDM.rotation;
                        this.contentContainer.add(textObject); // add the text object to container
                        tmpButtonDM.visualButton.data.set('textObject', textObject);
        
                    }


                } else if (tmpQuestionDM.type === 'match_and_draw_line_horizontal') {

                    let correctAnswer = tmpButtonDM.correctAnswers[0];

                    let visualSprite = tmpButtonDM.visualButton.data.get('visualSprite');
                    if (visualSprite !== undefined) {
                        visualSprite.destroy();
                        tmpButtonDM.visualButton.data.remove('visualSprite')
                    }

                    this.exerciseDM.destinationButtons.map(buttonDMDestination => {
                        if (correctAnswer !== buttonDMDestination.value) {
                            return null;
                        }

                        let lpTmpButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpButtonDM.x, tmpButtonDM.y);
                        let lpButtonDMDestination = this.calculateLocalXYFromGlobalXY(this.exerciseDM, buttonDMDestination.x, buttonDMDestination.y);
                        let x1 = lpTmpButtonDM.x + tmpButtonDM.w;
                        let x2 = lpButtonDMDestination.x;

                        let y1 = lpTmpButtonDM.y;
                        let y2 = lpButtonDMDestination.y;

                        let srcH = tmpButtonDM.h;
                        let dstH = buttonDMDestination.h;

                        if (x1 > x2) {
                            x1 = lpButtonDMDestination.x + buttonDMDestination.w;
                            x2 = lpTmpButtonDM.x;
    
                            y1 = lpButtonDMDestination.y;
                            y2 = lpTmpButtonDM.y;

                            srcH = buttonDMDestination.h;
                            dstH = tmpButtonDM.h;            
                        }
        
                        let spriteName = 'spriteMADHL' + uuidv4();
                        let thickness = this.exerciseDM.config.drawingLineThickness;
                        this.createMatchLineHorizontalObjectInTexture(spriteName,
                                                                        x1, y1, x2, y2,
                                                                        srcH, dstH,
                                                                        thickness, colorcode);
        
                        let x = x1;
                        let y = y1;
        
                        if (x1 > x2) {
                            x = x2;
                        } else {
                            x = x1;
                        }
        
                        if (y1 > y2) {
                            y = y2;
                        } else {
                            y = y1;
                        }
        
                        visualSprite = this.add.sprite(x, y, spriteName).setOrigin(0);
                        this.contentContainer.add(visualSprite);
                        tmpButtonDM.visualButton.data.set('visualSprite', visualSprite);

                        return null;
                    });



                } else if (tmpQuestionDM.type === 'match_and_draw_line_vertical') {

                    let correctAnswer = tmpButtonDM.correctAnswers[0];

                    let visualSprite = tmpButtonDM.visualButton.data.get('visualSprite');
                    if (visualSprite !== undefined) {
                        visualSprite.destroy();
                        tmpButtonDM.visualButton.data.remove('visualSprite')
                    }

                    this.exerciseDM.destinationButtons.forEach(buttonDMDestination => {
                        if (correctAnswer !== buttonDMDestination.value) {
                            return;
                        }

                        let lpTmpButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpButtonDM.x, tmpButtonDM.y);
                        let lpButtonDMDestination = this.calculateLocalXYFromGlobalXY(this.exerciseDM, buttonDMDestination.x, buttonDMDestination.y);
                        
                        let x1 = lpTmpButtonDM.x;
                        let x2 = lpButtonDMDestination.x;
        
                        let y1 = lpTmpButtonDM.y + tmpButtonDM.h;
                        let y2 = lpButtonDMDestination.y;
        
                        let srcW = tmpButtonDM.w;
                        let dstW = buttonDMDestination.w;


                        if (y1 > y2) {
                            x1 = lpButtonDMDestination.x;
                            x2 = lpTmpButtonDM.x;
            
                            y1 = lpButtonDMDestination.y + buttonDMDestination.h;
                            y2 = lpTmpButtonDM.y;
            
                            srcW = buttonDMDestination.w;
                            dstW = tmpButtonDM.w;            
                        }
        
                        let spriteName = 'spriteMADVL' + uuidv4();
                        let thickness = this.exerciseDM.config.drawingLineThickness;
                        this.createMatchLineVerticalObjectInTexture(spriteName,
                                                                        x1, y1, x2, y2,
                                                                        srcW, dstW,
                                                                        thickness, colorcode);

                        let x = x1;
                        let y = y1;

                        if (x1 > x2) {
                            x = x2;
                        } else {
                            x = x1;
                        }

                        if (y1 > y2) {
                            y = y2;
                        } else {
                            y = y1;
                        }
        
                        visualSprite = this.add.sprite(x, y, spriteName).setOrigin(0);
                        this.contentContainer.add(visualSprite);
                        tmpButtonDM.visualButton.data.set('visualSprite', visualSprite);

                
                    });
                } else if (tmpQuestionDM.type === 'match_words_to_make_sentence') {

                    // now it is time to follow the correctAnswers of the chain
                    let currentTmpButtonDM = tmpButtonDM;
                    let iterationCounter = 0; // this is backup exit condition controller to prevent more than 10 times iteration

                    while (iterationCounter < 10 && currentTmpButtonDM.correctAnswers.length > 0) {

                        let correctAnswer = currentTmpButtonDM.correctAnswers[0];

                        this.exerciseDM.destinationButtons.map(buttonDMDestination => {
                            if (correctAnswer !== buttonDMDestination.value) {
                                return null;
                            }
    

                            // first remove the existing user answer if there is any
                            let visualSprite = currentTmpButtonDM.visualButton.data.get('visualSprite');
                            if (visualSprite !== undefined) {
                                visualSprite.destroy();
                                currentTmpButtonDM.visualButton.data.remove('visualSprite')
                            }

                            let lpCurrentTmpButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, currentTmpButtonDM.x, currentTmpButtonDM.y);
                            let lpButtonDMDestination = this.calculateLocalXYFromGlobalXY(this.exerciseDM, buttonDMDestination.x, buttonDMDestination.y);
    

                            let x1 = lpCurrentTmpButtonDM.x + currentTmpButtonDM.w;
                            let x2 = lpButtonDMDestination.x;
    
                            let y1 = lpCurrentTmpButtonDM.y;
                            let y2 = lpButtonDMDestination.y;
    
                            let srcH = currentTmpButtonDM.h;
                            let dstH = buttonDMDestination.h;
    
                            if (x1 > x2) {
                                x1 = lpButtonDMDestination.x + buttonDMDestination.w;
                                x2 = lpCurrentTmpButtonDM.x;
        
                                y1 = lpButtonDMDestination.y;
                                y2 = lpCurrentTmpButtonDM.y;
    
                                srcH = buttonDMDestination.h;
                                dstH = currentTmpButtonDM.h;            
                            }
            
                            let spriteName = 'spriteMADHL' + uuidv4();
                            let thickness = this.exerciseDM.config.drawingLineThickness;
                            this.createMatchLineHorizontalObjectInTexture(spriteName,
                                                                            x1, y1, x2, y2,
                                                                            srcH, dstH,
                                                                            thickness, colorcode);
            
                            let x = x1;
                            let y = y1;
            
                            if (x1 > x2) {
                                x = x2;
                            } else {
                                x = x1;
                            }
            
                            if (y1 > y2) {
                                y = y2;
                            } else {
                                y = y1;
                            }
            
                            visualSprite = this.add.sprite(x, y, spriteName).setOrigin(0);
                            this.contentContainer.add(visualSprite);
                            currentTmpButtonDM.visualButton.data.set('visualSprite', visualSprite);
    
                            currentTmpButtonDM = buttonDMDestination; // siwtch to next item of the chain
                            return null;
                        });
                        iterationCounter++;
                    }

                    

                } else if (tmpQuestionDM.type === 'crossword_puzzle') {

                    let fontSize = this.exerciseDM.config.fontSize;

                    // first remove old written items
                    for (let i = 0; i < tmpButtonDM.labelModels.length; i++) {
                        let labelDM = tmpButtonDM.labelModels[i];
                        let text = labelDM.textObject;
                        if (text !== undefined && text !== null) {
                            text.destroy();
                            labelDM.textObject = null;
                        }
                    }


                    let answer = tmpButtonDM.correctAnswers[0];
                    let tmpAnswer = answer.replace(/ /g, '');

                    for (let i = 0; i < tmpAnswer.length && i < tmpButtonDM.labelModels.length; i++) {
                        let labelDM = tmpButtonDM.labelModels[i];
                        if (labelDM.skip) {
                            continue;
                        }

                        let currentChar = tmpAnswer.charAt(i);
                        if (tmpButtonDM.displayCase === 'uppercase') {
                            currentChar = currentChar.toUpperCase();
                        } else if (tmpButtonDM.displayCase === 'lowercase') {
                            currentChar = currentChar.toLowerCase();
                        }


                        let text = labelDM.textObject;
                        if (text !== undefined && text !== null) {
                            text.destroy();
                            labelDM.textObject = null;
                        }

                        let lpLabelDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, labelDM.x, labelDM.y);

                        text = this.createTextObject(lpLabelDM.x, lpLabelDM.y,
                            labelDM.w, labelDM.h, currentChar, 'center', fontSize, colorcode);
                        this.contentContainer.add(text);

                        labelDM.textObject = text;
                    }

                } else if (tmpQuestionDM.type === 'coloring') {

                    let polygon = tmpButtonDM.visualButton.data.get('visualSprite');
                    if (polygon !== undefined) {
                        polygon.destroy();
                        tmpButtonDM.visualButton.data.remove('textObject');
                    }

                    let correctAnswerStr = tmpButtonDM.correctAnswers[0];

                    let correctAnswerColorDM = ColorsDM.findColorModelByName(correctAnswerStr);
                    let lpTmpButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpButtonDM.x, tmpButtonDM.y);
                    polygon = this.createColoringPolygon(lpTmpButtonDM.x, lpTmpButtonDM.y, 
                        zoomInitX, zoomInitY, tmpButtonDM.pathModels, correctAnswerColorDM.colorcode);
                    this.contentContainer.add(polygon);
                    tmpButtonDM.visualButton.data.set('visualSprite', polygon);


                } else if (tmpQuestionDM.type === 'match_and_write_to_a_box' 
                            || tmpQuestionDM.type === 'fill_in_the_blanks'
                            || tmpQuestionDM.type === 'fill_in_the_blanks_grouping'
                            || tmpQuestionDM.type === 'match_and_write_to_a_group_box'
                            || tmpQuestionDM.type === 'write_t_or_f'
                            || tmpQuestionDM.type === 'write_y_or_n'
                            || tmpQuestionDM.type === 'write_true_or_false'
                            || tmpQuestionDM.type === 'write_tick_or_cross'
                            || tmpQuestionDM.type === 'write_from_selection_box'
                            || tmpQuestionDM.type === 'write_yes_or_no') {


                    let fontSize = this.exerciseDM.config.fontSize;

                    let textObject = tmpButtonDM.visualButton.data.get('textObject');
                    if (textObject !== undefined) {
                        textObject.destroy();
                        tmpButtonDM.visualButton.data.remove('textObject');
                        textObject = null;
                    }
    
                    let lpTmpButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpButtonDM.x, tmpButtonDM.y);
                    let correctAnswer = tmpButtonDM.correctAnswers[0];
                    if (correctAnswer === 'True' && tmpQuestionDM.type === 'write_tick_or_cross') {
                        textObject = this.createTextObject(lpTmpButtonDM.x, lpTmpButtonDM.y,
                            tmpButtonDM.w, tmpButtonDM.h, 'P', tmpButtonDM.align, fontSize + 8, colorcode, 'wingdings2');
                    } else if (correctAnswer === 'False' && tmpQuestionDM.type === 'write_tick_or_cross') {
                        textObject = this.createTextObject(lpTmpButtonDM.x, lpTmpButtonDM.y,
                            tmpButtonDM.w, tmpButtonDM.h, 'O', tmpButtonDM.align, fontSize + 8, colorcode, 'wingdings2');
                    } else if (tmpQuestionDM.type === 'write_t_or_f'
                                || tmpQuestionDM.type === 'write_true_or_false'
                                || tmpQuestionDM.type === 'write_yes_or_no') {
                        let text = '';
                        if (tmpQuestionDM.type === 'write_t_or_f') {
                            if (tmpQuestionDM.alternateValues.length >= 2) {
                                text = correctAnswer === 'True' ? tmpQuestionDM.alternateValues[0] : tmpQuestionDM.alternateValues[1];
                            } else {
                                text = correctAnswer === 'True' ? 'T' : 'F';
                            }
                        } else if (tmpQuestionDM.type === 'write_true_or_false') {
                            text = correctAnswer === 'True' ? 'True' : 'False';
                        } else if (tmpQuestionDM.type === 'write_yes_or_no') {
                            text = correctAnswer === 'True' ? 'Yes' : 'No';
                        } 
                        textObject = this.createTextObject(lpTmpButtonDM.x, lpTmpButtonDM.y,
                            tmpButtonDM.w, tmpButtonDM.h, text, tmpButtonDM.align, fontSize, colorcode);
                    
                    } else if (correctAnswer === 'True' && tmpQuestionDM.type === 'write_from_selection_box') {
                        let text = tmpQuestionDM.alternateValues[0];
                        textObject = this.createTextObject(lpTmpButtonDM.x, lpTmpButtonDM.y,
                            tmpButtonDM.w, tmpButtonDM.h, text, tmpButtonDM.align, fontSize, colorcode);
                    } else if (correctAnswer === 'False' && tmpQuestionDM.type === 'write_from_selection_box') {
                        let text = tmpQuestionDM.alternateValues[1];
                        textObject = this.createTextObject(lpTmpButtonDM.x, lpTmpButtonDM.y,
                            tmpButtonDM.w, tmpButtonDM.h, text, tmpButtonDM.align, fontSize, colorcode);
                    } else if (tmpQuestionDM.type === 'fill_in_the_blanks_grouping'|| tmpQuestionDM.type === 'match_and_write_to_a_group_box') {
                        if (tmpQuestionDM.groupingAnswers.length > tmpButtonDMIndex) {
                            let answer = tmpQuestionDM.groupingAnswers[tmpButtonDMIndex];
                            textObject = this.createTextObject(lpTmpButtonDM.x, lpTmpButtonDM.y,
                                tmpButtonDM.w, tmpButtonDM.h, answer, tmpButtonDM.align, fontSize, colorcode, 'Arial', tmpButtonDM.rotation);
                        } else {
                            return null;
                        }
                    } else if (tmpQuestionDM.type === 'match_and_write_to_a_box') {
                        let lpTmpButtonDMTarget = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpButtonDM.targetx, tmpButtonDM.targety);
                        textObject = this.createTextObject(lpTmpButtonDMTarget.x, lpTmpButtonDMTarget.y,
                            tmpButtonDM.targetw, tmpButtonDM.targeth, correctAnswer, tmpButtonDM.align, fontSize, colorcode, 'Arial', tmpButtonDM.rotation);
                    } else {
                        textObject = this.createTextObject(lpTmpButtonDM.x, lpTmpButtonDM.y,
                            tmpButtonDM.w, tmpButtonDM.h, correctAnswer, tmpButtonDM.align, fontSize, colorcode, 'Arial', tmpButtonDM.rotation);
                    }
                    textObject.angle = tmpButtonDM.rotation;
                    this.contentContainer.add(textObject); // add the textObject to container
                    tmpButtonDM.visualButton.data.set('textObject', textObject);

                    if (tmpQuestionDM.type === 'match_and_write_to_a_box' || tmpQuestionDM.type === 'match_and_write_to_a_group_box') {
                        // remove previous cross from selection and draw a new one
                        this.exerciseDM.destinationButtons.forEach(tmpDstButtonDM => {
                            if (correctAnswer !== tmpDstButtonDM.value) {
                                return;
                            }

                            let visualSprite = tmpDstButtonDM.visualButton.data.get('visualSprite');
                            if (visualSprite !== undefined) {

                                visualSprite.destroy();
                                tmpDstButtonDM.visualButton.data.remove('visualSprite')
                            }

                            let spriteName = 'spriteMAWTAB' + uuidv4();
                            let thickness = this.exerciseDM.config.drawingLineThickness;
                            this.createUsedDestinationCrossObjectInTexture(spriteName, 
                                tmpDstButtonDM.w, tmpDstButtonDM.h,
                                            thickness, colorcode);

                            let lpTmpDstButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpDstButtonDM.x, tmpDstButtonDM.y);
                            visualSprite = this.add.sprite(lpTmpDstButtonDM.x,
                                                                lpTmpDstButtonDM.y, spriteName).setOrigin(0);
                            this.contentContainer.add(visualSprite); // add the cross sprite to container
                            tmpDstButtonDM.visualButton.data.set('visualSprite', visualSprite);
                        });
                    }
                }



                let tween = tmpButtonDM.visualButton.data.get('tween');
                if (tween !== undefined && tween !== null) {
                    tween.pause();
                }
                tmpButtonDM.visualButton.alpha = 0.0001; // if it is set to zero, then input hooks does not work

            });

            tmpQuestionDM.words.forEach(word => {

                if (this.exerciseDM.puzzleFindWordsDM == null) {
                    return;
                }

                let pfwDM = this.exerciseDM.puzzleFindWordsDM;

                let buttons2D = pfwDM.buttons;
                let srcRow = word.srcRow;
                let srcCol = word.srcCol;
                let dstRow = word.dstRow;
                let dstCol = word.dstCol;

                let srcButtonDM = buttons2D[srcRow][srcCol];
                let dstButtonDM = buttons2D[dstRow][dstCol];

                let x = srcButtonDM.x;
                let y = srcButtonDM.y;
                let w = dstButtonDM.x + dstButtonDM.w - x;
                let h = dstButtonDM.y + dstButtonDM.h - y;
                
                // remove all previous answers
                buttons2D.forEach(col => {
                    col.forEach(btn => {
                        let answers = [];
                        btn.currentAnswers.forEach(answerDM => {
                            if (answerDM.isAnswerShown !== undefined && answerDM.isAnswerShown) {
                                answers.push(answerDM);
                                return;
                            }

                            if (answerDM.visualResultIndicator !== undefined && answerDM.visualResultIndicator !== null) {
                                answerDM.visualResultIndicator.destroy();
                                answerDM.visualResultIndicator = null;
                            }

                            if (answerDM.visualSprite !== undefined && answerDM.visualSprite !== null) {
                                answerDM.visualSprite.destroy();
                                answerDM.visualSprite = null;
                            }
                        });
                        btn.currentAnswers = answers;

                        let tween = btn.visualButton.data.get('tweenSrc');
                        if (tween !== undefined && tween !== null) {
                            tween.pause();
                        }
                        tween = btn.visualButton.data.get('tweenDst');
                        if (tween !== undefined && tween !== null) {
                            tween.pause();
                        }
                        btn.visualButton.alpha = 0.0000; // if it is set to zero, then input hooks does not work
                    });
                });


                let thickness = this.exerciseDM.config.drawingLineThickness;

                let spriteName = 'spritePFW' + uuidv4();
                this.createRoundedRectangleFillObjectInTexture(spriteName, w, h, colorcode, thickness, 8);
                let spritePFW = this.add.sprite(x, y, spriteName).setOrigin(0);
                let newAnswer = {
                    srcRow: srcRow,
                    srcCol: srcCol,
                    dstRow: dstRow,
                    dstCol: dstCol,
                    visualSprite: spritePFW,
                    visualResultIndicator: null,
                    isAnswerShown: true
                };
        
                srcButtonDM.currentAnswers.push(newAnswer);
                pfwDM.visualContainer.add(spritePFW);
            });

            tmpQuestionDM.isAnswerShown = true;
            anAnswerIsShown = true;
        });
    }





    createTextObject(x, y, width, height, text, align, size, color, fontface = 'Arial', rotation = 0) {
        let colorStr = toHexadecimalStringColorCode(color);
        
        let config = {
            fontFamily: fontface, 
            fontSize: size, 
            color: colorStr,
            align: align,
            fixedWidth: width,
            fixedHeight: 0,
            wordWrap: {
                width: width
            }
        };

        let myText;
        if (rotation === 0) {
            // setOrigin(0.5) is i mportant for vertically align middle effect
            myText = this.add.text(x + width / 2, y + height / 2, text, config).setOrigin(0.5); 
        } else {
            // if the rotation is used then forget about vertical alignn and just align it on the place.
            myText = this.add.text(x, y, text, config).setOrigin(0);
            myText.angle = rotation;
        }

        return myText;
    }

    createUsedDestinationCrossObjectInTexture(key, width, height, thickness, color) {
        let graphics = new Phaser.GameObjects.Graphics(this);

        graphics.lineStyle(thickness, color, 1);

        let line = new Phaser.Geom.Line(0, 0, width, height);
        graphics.strokeLineShape(line);
        line = new Phaser.Geom.Line(0, height, width, 0);
        graphics.strokeLineShape(line);

        if (this.textures.exists(key)) { // delete for reusing
            this.textures.remove(key);
        }
        graphics.generateTexture(key, width, height);
    }

    createMatchLineHorizontalObjectInTexture(key, x1, y1, x2, y2, h1, h2, thickness, color) {
        let graphics = new Phaser.GameObjects.Graphics(this);

        graphics.lineStyle(thickness, color, 1);


        let nx1 = 0;
        let ny1 = 0;
        let nx2 = 0;
        let ny2 = 0;

        if (x1 > x2) {
            nx1 = x1 - x1;
            nx2 = x1 - x2;
        } else {
            nx1 = x2 - x2;
            nx2 = x2 - x1;
        }

        if (y1 > y2) {
            ny1 = y1 - y2 + h1 / 2;
            ny2 = y2 - y2 + h2 / 2;
        } else {
            ny1 = y1 - y1 + h1 / 2;
            ny2 = y2 - y1 + h2 / 2;
        }

        let startPoint = new Phaser.Math.Vector2(nx1, ny1);
        let controlPoint1 = new Phaser.Math.Vector2(Phaser.Math.Between(nx1 + nx2/8, nx2 - nx2/8), 
                                                    Phaser.Math.Between(ny1 + ny2/8, ny2 - ny2/8));
        let controlPoint2 = new Phaser.Math.Vector2(Phaser.Math.Between(nx1 + nx2/8, nx2 - nx2/8), 
                                                    Phaser.Math.Between(ny1 + ny2/8, ny2 - ny2/8));
        let endPoint = new Phaser.Math.Vector2(nx2, ny2);

        let curve = new Phaser.Curves.CubicBezier(startPoint, controlPoint1, controlPoint2, endPoint);

        curve.draw(graphics);

        if (this.textures.exists(key)) { // delete for reusing
            this.textures.remove(key);
        }
        let width = Math.abs(nx1 - nx2);
        let height = 0;
        if (y1 > y2) {
            height = Math.abs(ny1 - ny2) + h2;
        } else {
            height = Math.abs(ny1 - ny2) + h1;
        }

        if (width === 0) {
            width = thickness;
        } 
        if (height === 0) {
            height = thickness;
        }
        graphics.generateTexture(key, width, height);
    }


    createMatchLineVerticalObjectInTexture(key, x1, y1, x2, y2, w1, w2, thickness, color) {
        let graphics = new Phaser.GameObjects.Graphics(this);

        graphics.lineStyle(thickness, color, 1);


        let ny1 = 0;
        let nx1 = 0;
        let ny2 = 0;
        let nx2 = 0;

        if (x1 > x2) {
            nx1 = x1 - x2 + w1 / 2;
            nx2 = x2 - x2 + w2 / 2;
        } else {
            nx1 = x1 - x1 + w1 / 2;
            nx2 = x2 - x1 + w2 / 2;
        }

        if (y1 > y2) {
            ny1 = y1 - y1;
            ny2 = y1 - y2;
        } else {
            ny1 = y2 - y2;
            ny2 = y2 - y1;
        }


        let startPoint = new Phaser.Math.Vector2(nx1, ny1);
        let controlPoint1 = new Phaser.Math.Vector2(Phaser.Math.Between(nx1 + nx2/8, nx2 - nx2/8), 
                                                    Phaser.Math.Between(ny1 + ny2/8, ny2 - ny2/8));
        let controlPoint2 = new Phaser.Math.Vector2(Phaser.Math.Between(nx1 + nx2/8, nx2 - nx2/8), 
                                                    Phaser.Math.Between(ny1 + ny2/8, ny2 - ny2/8));
        let endPoint = new Phaser.Math.Vector2(nx2, ny2);

        let curve = new Phaser.Curves.CubicBezier(startPoint, controlPoint1, controlPoint2, endPoint);

        curve.draw(graphics);

        if (this.textures.exists(key)) { // delete for reusing
            this.textures.remove(key);
        }
        let height = Math.abs(ny1 - ny2);
        let width = 0;
        if (x1 > x2) {
            width = Math.abs(nx1 - nx2) + w2;
        } else {
            width = Math.abs(nx1 - nx2) + w1;
        }

        if (height === 0) {
            height = thickness;
        } 
        if (width === 0) {
            width = thickness;
        }
        graphics.generateTexture(key, width, height);
    }

    createRoundedRectangleFillObjectInTexture(key, width, height, color, thickness = 2, radius = 10) {
        let graphics = new Phaser.GameObjects.Graphics(this);
        graphics.lineStyle(thickness, color, 1);

        graphics.strokeRoundedRect(0, 0, width, height, radius);
        if (this.textures.exists(key)) { // delete for reusing
            this.textures.remove(key);
        }
        graphics.generateTexture(key, width, height);
    }

    createCircleObjectInTexture(key, width, height, margin, thickness, color) {

        let graphics = new Phaser.GameObjects.Graphics(this);
        // given points
        let x1 = margin;
        let y1 = margin;
        let x2 = margin;
        let y2 = margin + height;
        let x3 = margin + width;
        let y3 = margin + height;
        let x4 = margin + width;
        let y4 = margin;

        // calculated points
        let n1x = (x1 + x4) / 2 + margin / 2;
        let n1y = y1 - margin + thickness / 2;
        let n2x = x1 - margin + thickness / 2;
        let n2y = (y1 + y2) / 2;
        let n3x = (x2 + x3) / 2;
        let n3y = y2 + margin;
        let n4x = x4 + margin;
        let n4y = (y3 + y4) / 2;
        let n5x = (x1 + x4) / 2 - margin / 2;
        let n5y = y1 - margin / 3;

        let c1x = n2x;
        let c1y = n1y;
        let c2x = n2x;
        let c2y = n3y;
        let c3x = n4x;
        let c3y = n3y;
        let c4x = n4x;
        let c4y = n5y;

        graphics.lineStyle(thickness, color, 1);

        let startPoint = new Phaser.Math.Vector2(n1x, n1y);
        let controlPoint = new Phaser.Math.Vector2(c1x, c1y);
        let endPoint = new Phaser.Math.Vector2(n2x, n2y);
        let curve = new Phaser.Curves.QuadraticBezier(startPoint, controlPoint, endPoint);
        curve.draw(graphics);
        startPoint = new Phaser.Math.Vector2(n2x, n2y);
        controlPoint = new Phaser.Math.Vector2(c2x, c2y);
        endPoint = new Phaser.Math.Vector2(n3x, n3y);
        curve = new Phaser.Curves.QuadraticBezier(startPoint, controlPoint, endPoint);
        curve.draw(graphics);
        startPoint = new Phaser.Math.Vector2(n3x, n3y);
        controlPoint = new Phaser.Math.Vector2(c3x, c3y);
        endPoint = new Phaser.Math.Vector2(n4x, n4y);
        curve = new Phaser.Curves.QuadraticBezier(startPoint, controlPoint, endPoint);
        curve.draw(graphics);
        startPoint = new Phaser.Math.Vector2(n4x, n4y);
        controlPoint = new Phaser.Math.Vector2(c4x, c4y);
        endPoint = new Phaser.Math.Vector2(n5x, n5y);
        curve = new Phaser.Curves.QuadraticBezier(startPoint, controlPoint, endPoint);
        curve.draw(graphics);

        if (this.textures.exists(key)) { // delete for reusing
            this.textures.remove(key);
        }
        graphics.generateTexture(key, width + 2 * margin + thickness / 1.5, height + 2 * margin + thickness / 1.5);

    }

    createColoringPolygon(x, y, zoomInitX, zoomInitY, path, color) {
        let newPath = '';
        path.map(obj => {
            newPath += `${obj.x - zoomInitX - x} ${obj.y - zoomInitY - y} `;
            return null;
        });
        newPath = newPath.trim();
        let polygon = new Phaser.GameObjects.Polygon(this, x, y, newPath, color, 1).setOrigin(0);
        // polygon.blendMode = Phaser.BlendModes.MULTIPLY;  // does not work under CANVAS type
        polygon.alpha = 0.8;
        return polygon;
    }

    isPointInPolygon(x, y, pathArray) {
        let result = false;
        let pointsX = [];
        let pointsY = [];
        
        
        pathArray.map(point => {
            pointsX.push(point.x);
            pointsY.push(point.y);
            return null;
        });
        
        
        let polyCorners = (pointsX.length % 2) === 0 ? pointsX.length : (pointsX.length - 1);
        let k = -1;
        let l = -1;

        l = polyCorners - 1;
        
        for (k = 0; k < polyCorners; k++)
        {
            if ((pointsY[k] < y && pointsY[l] >= y) || (pointsY[l] < y && pointsY[k] >= y))
            {
                if (pointsX[k]+(y-pointsY[k])/(pointsY[l]-pointsY[k])*(pointsX[l]-pointsX[k])<x) 
                {
                    result=!result; 
                }
            }
            l=k;
        }
        
        return result;
    }

    createAndAddCorrectIndicatorImage(size, x, y, originX = 0.5, originY = 0.5) {
        let imageCorrectIndicator = this.add.sprite(x, y, 'correctIndicatorImage').setOrigin(originX, originY);
        let scale = size / 150;
        imageCorrectIndicator.scale = scale;
        return imageCorrectIndicator;
    }

    createAndAddIncorrectIndicatorImage(size, x, y, originX = 0.5, originY = 0.5) {
        let imageIncorrectIndicator = this.add.sprite(x, y, 'incorrectIndicatorImage').setOrigin(originX, originY);
        let scale = size / 150;
        imageIncorrectIndicator.scale = scale;
        return imageIncorrectIndicator;
    }

    checkAnsweredQuestionsHandler() {
/*
        alert('This is student\'s version of i-TEAM!\nAnswers are not check in this version.\nPlease, use teacher\'s version to check the answers.');
        return;
*/



        if (this.exerciseDM === null) {
            return;
        }

        let resultIndicatorSize = this.exerciseDM.config.answerResultIndicatorSize;

        this.exerciseDM.questions.forEach(tmpQuestionDM => {

            if (tmpQuestionDM.isAnswerShown) {
                // if the answer is shown skip it
                return;
            }

            tmpQuestionDM.buttons.forEach(tmpSourceButtonDM => {
                if (tmpSourceButtonDM.currentAnswers.length === 0) {
                    // if the question is not answered, skip it
                    return;
                }

                if (tmpSourceButtonDM.visualResultIndicator !== null) {
                    let indicator = tmpSourceButtonDM.visualResultIndicator;
                    let tween = indicator.data.get('tween');
                    this.tweens.remove(tween);
                    indicator.data.remove('tween');
                    indicator.destroy();
                    tmpSourceButtonDM.visualResultIndicator = null;
                }

                if (tmpQuestionDM.type === 'circle_the_correct_one' 
                        || tmpQuestionDM.type === 'put_a_tick_into_the_correct_one'
                        || tmpQuestionDM.type === 'match_and_draw_line_horizontal'
                        || tmpQuestionDM.type === 'match_and_draw_line_vertical'
                        || tmpQuestionDM.type === 'match_words_to_make_sentence'
                        || tmpQuestionDM.type === 'crossword_puzzle'
                        || tmpQuestionDM.type === 'match_and_write_to_a_box' 
                        || tmpQuestionDM.type === 'fill_in_the_blanks'
                        || tmpQuestionDM.type === 'write_t_or_f'
                        || tmpQuestionDM.type === 'write_y_or_n'
                        || tmpQuestionDM.type === 'write_true_or_false'
                        || tmpQuestionDM.type === 'write_tick_or_cross'
                        || tmpQuestionDM.type === 'write_from_selection_box'
                        || tmpQuestionDM.type === 'write_yes_or_no')
                {

                    let answerStr = tmpSourceButtonDM.currentAnswers[0];

                    let indicatorImageCreateAndAddFunction = null;

                    let isCorrectAnswerFound = false;

                    tmpSourceButtonDM.correctAnswers.forEach(tmpAnswerStr => {
                        if (isCorrectAnswerFound === true){
                            return;
                        }

                        if (tmpQuestionDM.type === 'write_t_or_f') {
                            tmpAnswerStr = tmpAnswerStr === 'True' ? 'T' : 'F';
                        } else if (tmpQuestionDM.type === 'write_y_or_n') {
                            tmpAnswerStr = tmpAnswerStr === 'True' ? 'Y' : 'N';
                        } else if (tmpQuestionDM.type === 'write_yes_or_no') {
                            tmpAnswerStr = tmpAnswerStr === 'True' ? 'Yes' : 'No';
                        } else if (tmpQuestionDM.type === 'write_tick_or_cross') {
                            tmpAnswerStr = tmpAnswerStr === 'True' ? 'P' : 'O';
                        }

                        if (tmpQuestionDM.type === 'crossword_puzzle') {
                            // cross word puzzle ignores case by default
                            tmpSourceButtonDM.ignoreCase = true;
                            answerStr = answerStr.replace(/ /g, '');
                            tmpAnswerStr = tmpAnswerStr.replace(/ /g, '');
                        }

                        tmpSourceButtonDM.ignoreCase = true; // ignore case globally

                        if (tmpSourceButtonDM.ignoreCase) {
                            answerStr = answerStr.toUpperCase();
                            tmpAnswerStr = tmpAnswerStr.toUpperCase();
                        }

                        const regex = /[,.!? ]*$/; // ignore all trailing punkchuation chars

                        if (answerStr.replace(regex, '') === tmpAnswerStr.replace(regex, '')) {
                            isCorrectAnswerFound = true;
                        }

                        if (isCorrectAnswerFound && tmpQuestionDM.type === 'match_words_to_make_sentence') {
                            // the first source -> destination evaluation is finished and if it is correct then isCorrectAnswerFound contains true
                            // now check the chain whether it contains an invalid matching.


                            let iterationCounter = 0; // this is backup exit condition controller to prevent more than 10 times iteration
                            let currentTmpButtonDM = tmpSourceButtonDM;

                            while (isCorrectAnswerFound && iterationCounter < 10 && currentTmpButtonDM.currentAnswers.length > 0) {

                                if (currentTmpButtonDM.correctAnswers.length === 0) {
                                    // on the last destination button position, break the loop
                                    break;
                                }

                                // evaluate currentTmpButtonDM answer
                                if (currentTmpButtonDM.correctAnswers[0] !== currentTmpButtonDM.currentAnswers[0]) {
                                    // not equal, break all the loops, we found the treasure.
                                    isCorrectAnswerFound = false;
                                    break;
                                }

                                let foundNextHopDM = null;
                                // iterate destination buttons to check the next answered hop
                                this.exerciseDM.destinationButtons.forEach(tmpDestinationButtonDM => {
                                    if (currentTmpButtonDM.currentAnswers[0] === tmpDestinationButtonDM.value) {
                                        // next user selected hop is found
                                        // let's move currentTmpButtonDM pointer to it.
                                        foundNextHopDM = tmpDestinationButtonDM;
                                    }
                                });

                                if (foundNextHopDM !== null) {
                                    currentTmpButtonDM = foundNextHopDM;
                                }
                                
                                iterationCounter++;
                            }

                            if (iterationCounter >= 10) { 
                                // means chain is broken, partially answered, it must be evaluated as false
                                isCorrectAnswerFound = false;
                            }
                        }

                    });


                    if (isCorrectAnswerFound) {
                        indicatorImageCreateAndAddFunction = this.createAndAddCorrectIndicatorImage.bind(this);
                    } else {
                        indicatorImageCreateAndAddFunction = this.createAndAddIncorrectIndicatorImage.bind(this)
                    }

                    let lpTmpSourceButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpSourceButtonDM.x, tmpSourceButtonDM.y);

                    let x = lpTmpSourceButtonDM.x + tmpSourceButtonDM.w ;
                    let y = lpTmpSourceButtonDM.y;

                    if (tmpQuestionDM.type === 'match_and_write_to_a_box') {
                        let lpTmpSourceButtonDMTarget = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpSourceButtonDM.targetx, tmpSourceButtonDM.targety);
                        x = lpTmpSourceButtonDMTarget.x + tmpSourceButtonDM.targetw;
                        y = lpTmpSourceButtonDMTarget.y;
                    }

                    let resultIndicatorImage = indicatorImageCreateAndAddFunction(resultIndicatorSize,
                                                                                    x, y, 0.5, 0.5);

                    resultIndicatorImage.setDataEnabled(true);

                    tmpSourceButtonDM.visualResultIndicator = resultIndicatorImage; // save indicator for future reference
                    this.contentContainer.add(resultIndicatorImage); // add indicator to visualContainer

                    let scale = resultIndicatorImage.scale;
                    let tween = this.tweens.add({
                    targets: resultIndicatorImage,
                    scale: {getStart: () => {return scale}, getEnd: () => {return scale * 3}},
                    yoyo: true,
                    repeat: 0,
                    duration: 200,
                    ease: 'easeInQuint'
                    });
                    resultIndicatorImage.data.set('tween', tween);

                    tmpQuestionDM.isChecked = true;

                } else if (tmpQuestionDM.type === 'coloring') {

                    let answerStr = tmpSourceButtonDM.currentAnswers[0];

                    let indicatorImageCreateAndAddFunction = this.createAndAddIncorrectIndicatorImage.bind(this);

                    let isCorrectAnswerFound = false;
                    tmpSourceButtonDM.correctAnswers.forEach(tmpAnswerStr => {
                        if (isCorrectAnswerFound === true){
                            return;
                        }

                        if (answerStr === tmpAnswerStr) {
                            indicatorImageCreateAndAddFunction = this.createAndAddCorrectIndicatorImage.bind(this);
                            isCorrectAnswerFound = true;
                        }
                    });

                    let lpTmpSourceButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpSourceButtonDM.x, tmpSourceButtonDM.y);

                    let x = lpTmpSourceButtonDM.x + tmpSourceButtonDM.w / 2;
                    let y = lpTmpSourceButtonDM.y + tmpSourceButtonDM.h / 2;

                    let resultIndicatorImage = indicatorImageCreateAndAddFunction(resultIndicatorSize,
                                                                                    x, y, 0.5, 0.5);

                    resultIndicatorImage.setDataEnabled(true);

                    tmpSourceButtonDM.visualResultIndicator = resultIndicatorImage; // save indicator for future reference
                    this.contentContainer.add(resultIndicatorImage); // add indicator to visualContainer

                    let scale = resultIndicatorImage.scale;
                    let tween = this.tweens.add({
                    targets: resultIndicatorImage,
                    scale: {getStart: () => {return scale}, getEnd: () => {return scale * 3}},
                    yoyo: true,
                    repeat: 0,
                    duration: 200,
                    ease: 'easeInQuint'
                    });
                    resultIndicatorImage.data.set('tween', tween);


                    tmpQuestionDM.isChecked = true;

                } else if (tmpQuestionDM.type === 'fill_in_the_blanks_grouping' || tmpQuestionDM.type === 'match_and_write_to_a_group_box') {

                    let answerStr = tmpSourceButtonDM.currentAnswers[0];

                    let indicatorImageCreateAndAddFunction = this.createAndAddIncorrectIndicatorImage.bind(this);

                    let isCorrectAnswerFound = false;
                    tmpQuestionDM.groupingAnswers.forEach(tmpAnswerStr => {
                        if (isCorrectAnswerFound === true){
                            return;
                        }

                        if (tmpSourceButtonDM.ignoreCase) {
                            answerStr = answerStr.toUpperCase();
                            tmpAnswerStr = tmpAnswerStr.toUpperCase();
                        }

                        if (answerStr === tmpAnswerStr) {
                            indicatorImageCreateAndAddFunction = this.createAndAddCorrectIndicatorImage.bind(this);
                            isCorrectAnswerFound = true;
                        }

                    });

                    let lpTmpSourceButtonDM = this.calculateLocalXYFromGlobalXY(this.exerciseDM, tmpSourceButtonDM.x, tmpSourceButtonDM.y);
                    let x = lpTmpSourceButtonDM.x + tmpSourceButtonDM.w;
                    let y = lpTmpSourceButtonDM.y;

                    let resultIndicatorImage = indicatorImageCreateAndAddFunction(resultIndicatorSize,
                                                                                    x, y, 0.5, 0.5);

                    resultIndicatorImage.setDataEnabled(true);

                    tmpSourceButtonDM.visualResultIndicator = resultIndicatorImage; // save indicator for future reference
                    this.contentContainer.add(resultIndicatorImage); // add indicator to visualContainer

                    let scale = resultIndicatorImage.scale;
                    let tween = this.tweens.add({
                        targets: resultIndicatorImage,
                        scale: {getStart: () => {return scale}, getEnd: () => {return scale * 3}},
                        yoyo: true,
                        repeat: 0,
                        duration: 200,
                        ease: 'easeInQuint'
                    });
                    resultIndicatorImage.data.set('tween', tween);                
                    
                    tmpQuestionDM.isChecked = true;

                } 

            });

        });

        if (this.exerciseDM.puzzleFindWordsDM !== null) {
            let pfwDM = this.exerciseDM.puzzleFindWordsDM;

            let buttons2D = pfwDM.buttons;

            buttons2D.forEach(rowArr => {
                rowArr.forEach(btn => {
                    
                    btn.currentAnswers.forEach(answerDM => {
                        if (answerDM.isAnswerShown !== undefined && answerDM.isAnswerShown) {
                            return;
                        }

                        if (answerDM.visualResultIndicator !== null) {
                            return;
                        }

                        let isCorrectAnswerFound = false;
                        let indicatorImageCreateAndAddFunction = this.createAndAddIncorrectIndicatorImage.bind(this);

                        this.exerciseDM.questions.forEach(tmpQuestionDM => {
                            if (isCorrectAnswerFound === true) {
                                return;
                            }

                            if (tmpQuestionDM.type !== 'find_the_words_puzzle') {
                                return;
                            }

                            
                            tmpQuestionDM.words.forEach(tmpWordDM => {
                                if (isCorrectAnswerFound === true) {
                                    return;
                                }

                                let srcRow = tmpWordDM.srcRow;
                                let srcCol = tmpWordDM.srcCol;
                                let dstRow = tmpWordDM.dstRow;
                                let dstCol = tmpWordDM.dstCol;

                                if (answerDM.srcRow === srcRow && answerDM.srcCol === srcCol 
                                    && 
                                    answerDM.dstRow === dstRow && answerDM.dstCol === dstCol)
                                {
                                    indicatorImageCreateAndAddFunction = this.createAndAddCorrectIndicatorImage.bind(this);
                                    isCorrectAnswerFound = true;
                                }
    
                            });

                            


                        });

                        let srcButtonDM = buttons2D[answerDM.srcRow][answerDM.srcCol];
                        let dstButtonDM = buttons2D[answerDM.dstRow][answerDM.dstCol];
                        let x = 0;
                        let y = 0;

                        if (answerDM.srcRow === answerDM.dstRow) { // horizontal
                            x = dstButtonDM.x + dstButtonDM.w;
                            y = srcButtonDM.y;
                        } else { // vertical
                            x = srcButtonDM.x + srcButtonDM.w;
                            y = srcButtonDM.y;
                        }




                        let resultIndicatorImage = indicatorImageCreateAndAddFunction(resultIndicatorSize,
                                                                                x, y, 0.5, 0.5);

                        resultIndicatorImage.setDataEnabled(true);

                        answerDM.visualResultIndicator = resultIndicatorImage; // save indicator for future reference
                        pfwDM.visualContainer.add(resultIndicatorImage); // add indicator to visualContainer
    
                        let scale = resultIndicatorImage.scale;
                        let tween = this.tweens.add({
                            targets: resultIndicatorImage,
                            scale: {getStart: () => {return scale}, getEnd: () => {return scale * 3}},
                            yoyo: true,
                            repeat: 0,
                            duration: 200,
                            ease: 'easeInQuint'
                        });
                        resultIndicatorImage.data.set('tween', tween);

                    });    
                });
            });
        }
    }

    // calculateLocalXYFromGlobalXY() returns {'x': 50, 'y': 100} like local result;
    // if the the global point is not found in any of the zoominits, then {'x': -10000, 'y': -10000} is returned
    // NOTE: it is very important
    // - in exercise zoominits can not overlap each other's rectangular area.
    // - it is very important to grab coordinates of zoominit in from left to right, top to bottom order.
    calculateLocalXYFromGlobalXY(exerciseDM, x, y) {
        let point = {'x': -10000, 'y': -10000};
        let newX = 0;
        exerciseDM.zoomInits.forEach(zoomInit => {
            if (x >= zoomInit.x && x <= (zoomInit.x + zoomInit.w) && y >= zoomInit.y && y <= (zoomInit.y + zoomInit.h)) {
                let localX = x + newX - zoomInit.x; // x grows by width of the zoominit
                let localY = y - zoomInit.y; // y is always aligned top accross all zoominits
                point ={'x': localX, 'y': localY};
            }
            newX += zoomInit.w;
        });
        return point;
    }

    destroyScene() {
        // when this function is called by scene lifecycle destroy event is dispatched
        //    then all the resources related with scene are automatically removed and freed
        console.log('I\'m dying dusty... Don\'t forget to housekeep');


        // first find existing VirtualKeyboardScene instance and remove it from scene manager
        this.game.scene.scenes.forEach(sceneIterator => {
            if (sceneIterator instanceof VirtualKeyboardScene) {
                this.game.scene.remove('VirtualKeyboardScene');
                return;
            }
        });


        this.events.removeAllListeners('destroy');



        // clear all destination button phaser objects, handlers, sprites, tweens, ... etc
        this.exerciseDM.destinationButtons.forEach(buttonDMDstTmp => {
            if (buttonDMDstTmp.visualButton !== undefined && buttonDMDstTmp.visualButton !== null) {
                buttonDMDstTmp.visualButton = null;
            }
        });

        this.exerciseDM.questions.forEach(questionDMTmp => {
            questionDMTmp.buttons.forEach(buttonDMSrcTmp => {
                if (buttonDMSrcTmp.visualButton !== undefined && buttonDMSrcTmp.visualButton !== null) {
                    buttonDMSrcTmp.visualButton = null;
                }
                buttonDMSrcTmp.currentAnswers = [];
                buttonDMSrcTmp.visualResultIndicator = null;
            });
            questionDMTmp.isChecked = false;
            questionDMTmp.isAnswerShown = false;
        });
        

        let pfwDM = this.exerciseDM.puzzleFindWordsDM;
        if (pfwDM !== undefined && pfwDM !== null && pfwDM.visualContainer !== null) {
            pfwDM.buttons.forEach(rowArr => {
                rowArr.forEach(buttonDMTmp => {
                    if (buttonDMTmp.visualButton === null) {
                        return;
                    }
                    buttonDMTmp.visualButton = null;
                    buttonDMTmp.currentAnswers = [];
                    buttonDMTmp.visualResultIndicator = null;
                });
            });

            pfwDM.visualContainer = null;
        }


        let removeList = [];
        let textureList = this.textures.list;
        Object.keys(textureList).map(key => {
            // spriteMAWTAB, spriteCTCO, spritePFW, spriteMADHL, spriteMADVL
            if (key.substr(0, 12) === 'spriteMAWTAB' || key.substr(0, 10) === 'spriteCTCO' 
                    || key.substr(0, 9) === 'spritePFW' || key.substr(0, 11) === 'spriteMADHL'
                    || key.substr(0, 11) === 'spriteMADVL')
            {
                removeList.push(key);
            }
            return null;
        });

        removeList.map(key => this.textures.remove(key));

    }
}

class SelectionBox extends Phaser.GameObjects.Container {
    constructor(scene, x, y, config) {
        super(scene);
        this.scene = scene;
        this.config = config;
        this.x = x;
        this.y = y;


        let itemWidth = 120;
        if (config.itemWidth !== undefined) {
            itemWidth = +config.itemWidth;
        }
        
        let itemHeight = 40;
        if (config.itemHeight !== undefined) {
            itemHeight = +config.itemHeight;
        }

        let fontFamily = 'Arial';
        if (this.config.questionDM.type === 'write_tick_or_cross') {
            fontFamily = 'wingdings2';
        }

        if (config.fontFamily !== undefined) {
            fontFamily = config.fontFamily;
        }

        let fontSize = 32;
        if (config.fontSize !== undefined) {
            fontSize = config.fontSize;
        }

        config.items.map((item, index) => {
            let rectangle = this.scene.add.rectangle(0, index * itemHeight, itemWidth, itemHeight, 0xf0f0f0, 1).setOrigin(0);
            rectangle.setStrokeStyle(2, 0xe0e0e0);
            rectangle.setInteractive();
            
            let configText = {
                fontFamily: fontFamily, 
                fontSize: fontSize, 
                color: '#2200ff',
                align: 'center',
                fixedWidth: itemWidth,
                wordWrap: {
                    width: itemWidth
                },
                shadow: {
                    offsetX: 2,
                    offsetY: 2,
                    color: '#000000',
                    blur: 0.2,
                    stroke: false,
                    fill: false
                },
            };
            let text = this.scene.add.text(itemWidth / 2, (index * itemHeight) + itemHeight / 2, item.text, configText).setOrigin(0.5);
            

            rectangle.on('pointerover', () => {
                rectangle.fillColor = 0x6666ff;
                text.setColor('#ffffff');
            }, this.scene);

            rectangle.on('pointerout', () => {
                rectangle.fillColor = 0xf0f0f0;
                text.setColor('#2200ff');
            }, this.scene);

            rectangle.on('pointerdown', () => {
                item.itemclickfn();
            }, this.scene);

            this.add(rectangle);
            this.add(text);
            return null;
        });
        let rectangle = this.scene.add.rectangle(1, 1, itemWidth - 2, config.items.length * itemHeight - 2, 0xffffff, 0).setOrigin(0);
        rectangle.setStrokeStyle(2, 0x999999);
        this.add(rectangle);
        rectangle = this.scene.add.rectangle(0, 0, itemWidth, config.items.length * itemHeight, 0xffffff, 0).setOrigin(0);
        rectangle.setStrokeStyle(1, 0xffffff);
        this.add(rectangle);
        this.setSize(itemWidth, config.items.length * itemHeight);
    }

    getConfig() {
        return this.config;
    }
}

export default PageExerciseScene;